/**
 * Views/Prenomina/TablaPrenomina.jsx
 *
 * Tabla principal de la prenómina con dos vistas:
 *
 * Vista 1 — Importes (tabla Excel)
 *   Columnas fijas: Empleado
 *   Columnas dinámicas: las que el usuario configuró (valor | importe | ambos)
 *   Columnas fijas finales: Otras Percepciones | Otras Deducciones | Neto | Estatus | Acciones
 *
 *   NOTA: No hay columna "Total Percepciones" ni "Total Deducciones".
 *   Todo lo que no está en columnas visibles cae en "Otras Percepciones" u "Otras Deducciones".
 *   Si no hay ninguna columna configurada, el 100% del valor estará en esas dos columnas.
 *
 * Vista 2 — Días e Incidencias
 *   Filas: empleados
 *   Columnas: cada día del periodo (1, 2, ... N)
 *   Celda: muestra resumen de incidencias. Click → Popover para editar cantidades por tipo.
 */

import React, { Component } from 'react'
import { Tag, Tooltip, Modal, message, Input, Dropdown, Menu } from 'antd'
import { ButtonItem } from '../../../../Components/Items'
import {
  CalculatorOutlined, ReloadOutlined, EditOutlined,
  DeleteOutlined, PlusOutlined, SettingOutlined,
  TableOutlined, CalendarOutlined, ImportOutlined,
  SafetyCertificateOutlined, DownloadOutlined, WarningOutlined, FilePdfOutlined,
  CloseCircleOutlined, MailOutlined, SyncOutlined, SearchOutlined, PrinterOutlined,
  FileExcelOutlined, DownOutlined, MoreOutlined,
  CaretUpOutlined, CaretDownOutlined, BankOutlined,
} from '@ant-design/icons'
import axios from 'axios'
import dayjs from 'dayjs'
import 'dayjs/locale/es'
import * as XLSX from 'xlsx-js-style'
import { saveAs } from 'file-saver'
import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import nominaNoTimbradosPdf from '../../../../Pdf/nominaNoTimbradosPdf'
import ModalImportarIncidenciasExcel from '../ImportarIncidenciasExcel/ModalImportarIncidenciasExcel'
import ModalImportarAsistencia from '../ImportarIncidenciasAsistencia/ModalImportarAsistencia'
import { netoDeLinea, ordenarLineas } from '../ordenLineas'
import { OPCIONES_FINIQUITO } from '../filtroFiniquito'
import {
  descargarListaRaya as descargarListaRayaExcel,
  descargarDispersion as descargarDispersionExcel,
  descargarDatosPorEmpleado as descargarDatosPorEmpleadoExcel,
  cargarLogoBiwo,
  inyectarLogo as inyectarLogoEnHojas,
} from '../listaRayaExcel'
import { construirDiasPeriodo, construirIndiceTipos } from '../incidenciasListaRaya'

pdfMake.vfs = pdfFonts.pdfMake.vfs

dayjs.locale('es')

// Tope de columnas de día de la rejilla de incidencias del Excel. Un periodo mensual llega a 31;
// más que eso es un periodo mal capturado y la hoja saldría ilegible.
const MAX_DIAS_REJILLA_EXCEL = 40

// ─── Estilos ─────────────────────────────────────────────────────────────────
const TH = {
  border: '1px solid #d0d0d0', background: '#f5f5f5',
  padding: '5px 7px', fontSize: 11, fontWeight: 600,
  color: '#555', whiteSpace: 'nowrap', textAlign: 'center',
}
const TD     = { border: '1px solid #d0d0d0', padding: '4px 7px', fontSize: 12, verticalAlign: 'middle' }
const TD_NUM = { ...TD, textAlign: 'right', fontFamily: 'monospace' }

// Ancho de la columna de código. En la vista de Incidencias es además el offset
// 'left' de la columna de Empleado, que va sticky pegada a ella: por eso es una
// constante y no un 56 suelto repetido en cinco lugares — desalinearlos deja el
// nombre encimado sobre el código al hacer scroll horizontal.
const ANCHO_COL_CODIGO = 68

const fmt = (n) =>
  typeof n === 'number'
    ? n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '0.00'

/**
 * Chip de totales de la barra: Percepciones · Deducciones · Neto.
 *
 * Se pinta uno por bloque cuando la prenómina trae finiquitos —Ordinaria, Finiquitos y Total—
 * porque una liquidación puede valer varias veces la nómina del periodo y en una sola cifra el
 * total se vuelve ilegible. Sin finiquitos se pinta uno solo, sin etiqueta, que es el de siempre.
 *
 * Los totales son SIEMPRE de todas las líneas del bloque, nunca de lo filtrado en pantalla: es el
 * mismo criterio del pie de las tablas, para que un filtro no se lea como el total de la nómina.
 */
const renderChipTotales = (etiqueta, totales, { fondo, borde, color, resaltado, hayFiltro } = {}) => (
  <span
    key={etiqueta || 'unico'}
    style={{
      display: 'flex', alignItems: 'center', gap: 12, fontSize: 12,
      background: fondo, border: `1px solid ${borde}`, borderRadius: 6,
      padding: '5px 12px', whiteSpace: 'nowrap',
    }}
  >
    {etiqueta && (
      <span style={{ color, fontWeight: 600 }}>
        {etiqueta}
        <span style={{ color: '#bbb', fontWeight: 400, marginLeft: 5 }}>
          {totales.empleados}
        </span>
      </span>
    )}
    <span style={{ color: '#888' }}>
      Percepciones <b style={{ color: '#262626' }}>{fmt(totales.percepciones)}</b>
    </span>
    <span style={{ color: '#888' }}>
      Deducciones <b style={{ color: '#262626' }}>{fmt(totales.deducciones)}</b>
    </span>
    <span style={{ color, fontWeight: resaltado ? 700 : 600, fontSize: resaltado ? 13 : 12 }}>
      Neto {fmt(totales.neto)}
    </span>
    {hayFiltro && <span style={{ color: '#aaa', fontSize: 11 }}>(toda la nómina)</span>}
  </span>
)

// La opción recomendada de un submenú de descargas: en negritas y con el color de la casa, para
// que se distinga de las variantes de excepción sin tener que leerlas todas.
const ESTILO_OPCION_DESTACADA = { fontWeight: 600, color: '#00BFD6' }

// Encabezado de una de las dos secciones (Nómina ordinaria / Finiquitos). Lo comparten la vista
// de Importes y la de Días e Incidencias para que el corte se vea igual en las dos.
const renderTituloSeccion = ({ titulo, subtitulo, cuenta, acento = '#00BFD6' }) => (
  <div style={{
    display: 'flex', alignItems: 'baseline', gap: 10, margin: '4px 0 6px',
    borderLeft: `3px solid ${acento}`, paddingLeft: 8,
  }}>
    <span style={{ fontWeight: 600, fontSize: 13, color: acento }}>{titulo}</span>
    <span style={{ fontSize: 11, color: '#aaa' }}>
      {cuenta} {cuenta === 1 ? 'empleado' : 'empleados'}
      {subtitulo ? ` · ${subtitulo}` : ''}
    </span>
  </div>
)

// Marca de la última importación de asistencia, por empleado (la escribe el backend en la línea
// al importar del reloj checador). Va en la celda del nombre: el fondo de las celdas de día ya lo
// usan el séptimo, el fin de semana y los días fuera de la relación laboral.
const MARCA_RELOJ = {
  calculada: {
    color: '#52c41a', fondo: ['#f6ffed', '#eef9e3'],
    texto: 'Asistencia calculada con el reloj checador',
  },
  noCalculada: {
    color: '#ff4d4f', fondo: ['#fff1f0', '#ffe8e6'],
    texto: 'No se calculó con el reloj: no tiene asignación vigente a un reloj checador',
  },
}

// null si la línea nunca pasó por una importación de asistencia: esa no se pinta.
const marcaReloj = (linea) => {
  if (linea.asistenciaRelojCalculada == null) return null
  const marca = linea.asistenciaRelojCalculada ? MARCA_RELOJ.calculada : MARCA_RELOJ.noCalculada
  const fecha = linea.asistenciaRelojImportadaAt
    ? dayjs(linea.asistenciaRelojImportadaAt).format('DD/MM/YYYY HH:mm')
    : null
  return { ...marca, titulo: fecha ? `${marca.texto} · importada el ${fecha}` : marca.texto }
}

// Paleta de colores para tipos de incidencia dinámicos
const DIAS_ES = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb']

const COLOR_PALETTE = [
  '#ff4d4f', '#faad14', '#52c41a', '#722ed1',
  '#13c2c2', '#eb2f96', '#1677ff', '#8c8c8c',
  '#fa8c16', '#2f54eb', '#389e0d', '#c41d7f',
]

/**
 * Convierte los TiposIncidencias del catálogo al formato interno usado en la tabla.
 * @param {Array} tipos - Array de TiposIncidencias del back { _id, clave, nombre, ... }
 * @returns {Array} [{ tipo, label, abrev, color }]
 */
function mapearTiposInc(tipos = []) {
  return tipos.map((t, i) => ({
    id:    t._id,
    tipo:  t.clave,
    label: t.nombre,
    abrev: t.clave ? (t.clave.length <= 4 ? t.clave.toUpperCase() : t.clave.slice(0, 3).toUpperCase()) : '',
    color: COLOR_PALETTE[i % COLOR_PALETTE.length],
  }))
}

// ─── Popover de incidencias por día ──────────────────────────────────────────
// Muestra inputs de cantidad para cada tipo. Se guarda al cerrar.
// ─── Celda inline estilo Excel con botón modal ────────────────────────────────
class CeldaIncidencia extends Component {
  constructor(props) {
    super(props)
    this.inputRef = React.createRef()
    this.state = { editando: false }
  }

  componentDidUpdate(prevProps) {
    // Actualizar el input cuando cambian las entradas desde fuera (sin estar editando)
    if (!this.state.editando && prevProps.entradas !== this.props.entradas) {
      if (this.inputRef.current) {
        this.inputRef.current.value = this.getTextoGuardado()
      }
    }
  }

  getTextoGuardado() {
    const { entradas = [], tiposInc = [] } = this.props
    return entradas
      .filter(e => e.cantidad > 0)
      .map(e => {
        const t = tiposInc.find(x => x.id === e.tipoIncidenciaId || x.tipo === e.tipo)
        return t ? `${e.cantidad}${t.tipo}` : null
      })
      .filter(Boolean)
      .join(' ')
  }

  onFocus() {
    if (this.inputRef.current) this.inputRef.current.value = this.getTextoGuardado()
    this.setState({ editando: true })
  }

  onKeyDown(e) {
    if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur() }
    if (e.key === 'Escape') {
      if (this.inputRef.current) this.inputRef.current.value = this.getTextoGuardado()
      this.setState({ editando: false })
      e.currentTarget.blur()
    }
  }

  onBlur() {
    const texto = this.inputRef.current ? this.inputRef.current.value : ''
    this.setState({ editando: false })
    if (texto.trim().toUpperCase() === this.getTextoGuardado().toUpperCase()) return
    this.parsearYGuardar(texto)
  }

  parsearYGuardar(texto) {
    const { tiposInc = [], onGuardar } = this.props
    const errores = []
    const entradas = []
    const tokens = texto.trim().toUpperCase().split(/[\s,]+/).filter(Boolean)

    for (const token of tokens) {
      const m = token.match(/^(\d+)([A-Z_]+)$/)
      if (!m) { errores.push(`"${token}": formato inválido — ej: 2RET`); continue }
      const cantidad = parseInt(m[1])
      const codigo = m[2]
      const t = tiposInc.find(x => x.tipo && x.tipo.toUpperCase() === codigo)
      if (!t) { errores.push(`"${codigo}" no existe en el catálogo`); continue }
      if (cantidad > 0) entradas.push({ tipo: t.tipo, tipoIncidenciaId: t.id, cantidad, descripcion: '' })
    }

    if (errores.length) {
      message.error(errores.join(' / '))
      if (this.inputRef.current) this.inputRef.current.value = this.getTextoGuardado()
    } else {
      onGuardar(entradas)
    }
  }

  render() {
    // `soloLectura` = la línea de este empleado ya está timbrada. Puede convivir
    // con líneas editables en la misma nómina cuando se reabre una parcialmente
    // timbrada, así que el bloqueo es por celda y no por tabla.
    const { onAbrirModal, soloLectura } = this.props
    const { editando } = this.state
    const guardado = this.getTextoGuardado()

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 2, padding: '0 2px' }}>
        <input
          ref={this.inputRef}
          defaultValue={guardado}
          readOnly={soloLectura}
          onFocus={soloLectura ? undefined : this.onFocus.bind(this)}
          onBlur={soloLectura ? undefined : this.onBlur.bind(this)}
          onKeyDown={soloLectura ? undefined : this.onKeyDown.bind(this)}
          placeholder="—"
          style={{
            width: 58,
            border: editando ? '1px solid #1677ff' : 'none',
            background: editando ? '#fff' : 'transparent',
            outline: 'none', fontSize: 11, textAlign: 'center',
            fontWeight: guardado ? 700 : 400,
            color: editando ? '#333' : (guardado ? '#333' : '#ccc'),
            cursor: soloLectura ? 'default' : 'text', borderRadius: 3, padding: '2px 3px',
          }}
        />
        {!soloLectura && (
          <button
            onClick={onAbrirModal}
            title="Editar con detalle"
            style={{
              background: '#fff', border: '1px solid #d0d0d0', borderRadius: 3,
              padding: '0 4px', cursor: 'pointer', fontSize: 11, color: '#aaa',
              lineHeight: '18px', height: 18, flexShrink: 0,
            }}
          >
            ···
          </button>
        )}
      </div>
    )
  }
}

// ─────────────────────────────────────────────────────────────────────────────
class TablaPrenomina extends Component {

  state = {
    // 'importes' | 'incidencias'. La preferencia de la empresa llega en la respuesta de
    // la bandeja y el padre ya la tiene puesta para cuando monta esto: a este paso sólo
    // se llega desde la bandeja, así que basta con leerla en el inicializador. Si la
    // bandeja falló, el respaldo es 'importes', que es como abría siempre.
    vistaActiva: this.props.vistaInicial === 'incidencias' ? 'incidencias' : 'importes',

    // Filtro de la barra: sólo afecta lo que se ve en pantalla (ver getLineasFiltradas)
    filtroEmpleado: '',

    // Orden de la tabla, por clic en los encabezados. null = el orden con el que
    // llegó la prenómina del servidor (apellido paterno, nombre). Vale para las
    // dos vistas porque las dos pintan getLineasFiltradas().
    ordenCampo: null,
    ordenDescendente: false,

    // Modal de incidencias por día
    modalInc: null,           // { empleadoId, diaPeriodo, fecha, titulo } | null
    modalIncCantidades: {},   // { [tipo]: cantidad }
    modalIncTexto: '',        // texto de entrada rápida "2RET 1FAL"
    modalIncErrores: [],      // errores de parseo

    // Modal de importación de incidencias desde asistencia
    modalImportarAsistenciaVisible: false,

    // La dispersión pide el catálogo de bancos al vuelo, así que puede tardar
    descargandoDispersion: false,

    // Modal de importación de incidencias desde Excel
    modalImportarExcelVisible: false,
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  getTiposInc() {
    return mapearTiposInc(this.props.tiposIncidencias || [])
  }

  // Filtro de vista por código / nombre / RFC, y orden por clic en los encabezados.
  // Es SÓLO para lo que se pinta en las dos tablas: la Lista de Raya, los totales del
  // pie y las acciones masivas (calcular todo, recibos, importar asistencia) siguen
  // trabajando sobre prenomina.lineas completo, porque son documentos y operaciones de
  // nómina y no deben depender de lo que se esté filtrando.
  getLineasFiltradas() {
    const lineas = this.props.prenomina.lineas || []
    const q = (this.state.filtroEmpleado || '').trim().toLowerCase()
    const filtradas = !q ? lineas : lineas.filter(l => {
      const cod = l.codigo == null ? '' : String(l.codigo)
      // Se incluyen el código crudo y el rellenado con ceros para que dé lo mismo teclear
      // "12" o "0012" (en pantalla se ve rellenado).
      const heno = [
        cod, cod.padStart(4, '0'), l.serieNumeroEmpleado || '',
        l.apellidoPaterno || '', l.apellidoMaterno || '', l.nombre || '', l.rfc || '',
      ].join(' ').toLowerCase()
      return heno.includes(q)
    })

    const { ordenCampo, ordenDescendente } = this.state
    // Sin orden elegido se respeta el del servidor; ordenarLineas siempre devuelve
    // una copia, así que nunca se reacomoda prenomina.lineas.
    if (!ordenCampo) return filtradas
    return ordenarLineas(filtradas, ordenCampo, ordenDescendente, this.getNoSumaSet(), this.getEspecieSet())
  }

  /**
   * Clic en un encabezado ordenable: ascendente → descendente → sin orden.
   * El tercer clic devuelve la tabla al orden con el que llegó del servidor.
   */
  alternarOrden(campo) {
    this.setState(({ ordenCampo, ordenDescendente }) => {
      if (ordenCampo !== campo)  return { ordenCampo: campo, ordenDescendente: false }
      if (!ordenDescendente)     return { ordenCampo: campo, ordenDescendente: true }
      return { ordenCampo: null, ordenDescendente: false }
    })
  }

  // La flecha sale SÓLO en la columna activa: con las columnas dinámicas de
  // conceptos, una flecha apagada en cada encabezado es puro ruido.
  renderFlechaOrden(campo) {
    if (this.state.ordenCampo !== campo) return null
    const Flecha = this.state.ordenDescendente ? CaretDownOutlined : CaretUpOutlined
    return <Flecha style={{ fontSize: 9, marginLeft: 2 }} />
  }

  // `llave` sólo hace falta donde el <th> se devuelve dentro de un arreglo.
  renderThOrdenable(campo, etiqueta, { estilo = {}, rowSpan, llave } = {}) {
    return (
      <th key={llave} style={{ ...TH, ...estilo }} rowSpan={rowSpan}>
        <span
          onClick={() => this.alternarOrden(campo)}
          style={{ cursor: 'pointer', userSelect: 'none' }}
          title={`Ordenar por ${etiqueta.toLowerCase()}`}
        >
          {etiqueta}{this.renderFlechaOrden(campo)}
        </span>
      </th>
    )
  }

  // Código y clave comparten celda —igual que en el renglón, donde el EMPn va en
  // gris debajo del código—, así que el encabezado lleva dos botones de orden en
  // vez de uno.
  renderThCodigo({ estilo = {}, rowSpan, llave } = {}) {
    const boton = (campo, texto, extra = {}) => (
      <div
        onClick={() => this.alternarOrden(campo)}
        style={{ cursor: 'pointer', userSelect: 'none', ...extra }}
        title={`Ordenar por ${campo === 'codigo' ? 'código' : 'clave'}`}
      >
        {texto}{this.renderFlechaOrden(campo)}
      </div>
    )
    return (
      <th
        key={llave}
        style={{ ...TH, width: ANCHO_COL_CODIGO, minWidth: ANCHO_COL_CODIGO, ...estilo }}
        rowSpan={rowSpan}
      >
        {boton('codigo', 'Cód.')}
        {boton('clave', 'clave', { fontSize: 9, fontWeight: 400, color: '#aaa' })}
      </th>
    )
  }

  getColumnas() {
    const { prenomina, conceptosNomina } = this.props
    return (prenomina.columnasVisibles || []).map(col => {
      const def = conceptosNomina.find(c => c._id === col.conceptoNominaId)
      return def ? { ...col, def } : null
    }).filter(Boolean)
  }

  getCeldaValores(linea, columna) {
    const c = linea.conceptos?.find(x => x.conceptoNominaId === columna.conceptoNominaId)
    if (!c) return { valor: 0, importe: 0, esManualValor: false, esManualImporte: false }

    const esManualValor   = c.valorManual        !== null && c.valorManual        !== undefined
    const esManualImporte = c.importeTotalManual !== null && c.importeTotalManual !== undefined

    return {
      valor:          esManualValor   ? c.valorManual        : (c.valor        || 0),
      importe:        esManualImporte ? c.importeTotalManual : (c.importeTotal || 0),
      esManualValor,
      esManualImporte,
    }
  }

  getNoSumaSet() {
    return new Set(
      (this.props.conceptosNomina || [])
        .filter(c => c.noSuma)
        .map(c => c._id?.toString())
    )
  }

  // Percepciones en especie: se ven con su importe pero no suman al neto, porque no se le
  // entregan en dinero al empleado. Set aparte del de noSuma a propósito — éstas sí se
  // timbran y sí gravan, así que no se pintan como informativas.
  getEspecieSet() {
    return new Set(
      (this.props.conceptosNomina || [])
        .filter(c => c.tipo === 1 && c.especie && !c.noSuma)
        .map(c => c._id?.toString())
    )
  }

  // El cálculo vive en ordenLineas.js: tanto el encabezado "Neto" de esta tabla
  // como el visor de recibos ordenan por este mismo neto, y si las definiciones
  // se separaran el orden no cuadraría con lo que muestra la columna.
  getNetoLocal(linea) {
    return netoDeLinea(linea, this.getNoSumaSet(), this.getEspecieSet())
  }

  /**
   * Percepciones, deducciones y neto de un conjunto de líneas.
   *
   * Misma regla que getNetoLocal —los conceptos noSuma no entran en ningún
   * total— pero resolviendo el set de noSuma UNA vez en lugar de por línea, que
   * es lo que haría llamar a getNetoLocal en un reduce sobre toda la nómina.
   *
   * Se calcula aquí y no se lee prenomina.totalNeto a propósito: el total
   * persistido en el header puede quedar desfasado si se editaron importes
   * manuales sin recalcular, y noSuma se resuelve contra el catálogo vivo. Lo
   * que se muestre tiene que cuadrar con el pie de la tabla.
   */
  getTotalesNomina(lineas) {
    const noSumaIds = this.getNoSumaSet()
    const especieIds = this.getEspecieSet()
    const importeEfectivo = (c) => {
      const esManual = c.importeTotalManual !== null && c.importeTotalManual !== undefined
      return esManual ? c.importeTotalManual : (c.importeTotal || 0)
    }
    let percepciones = 0, especie = 0, deducciones = 0
    for (const linea of (lineas || [])) {
      for (const c of (linea.conceptos || [])) {
        const id = c.conceptoNominaId?.toString()
        if (noSumaIds.has(id) || c.noSuma) continue
        if (c.tipo === 1) {
          // En especie: se totaliza aparte y queda fuera del neto — no es entrega en dinero.
          // El catálogo manda, con respaldo en el flag del snapshot de la línea: si la
          // proyección que alimenta `conceptosNomina` llegara sin la bandera, el concepto
          // volvería a sumar en silencio.
          if (especieIds.has(id) || c.especie) especie += importeEfectivo(c)
          else percepciones += importeEfectivo(c)
        } else if (c.tipo === 2) deducciones += importeEfectivo(c)
      }
    }
    return {
      empleados: (lineas || []).length,
      percepciones,
      especie,
      deducciones,
      neto: percepciones - deducciones,
    }
  }

  /**
   * Datos del reporte de empleados que no se pudieron timbrar. Lo consumen tanto
   * el Excel como el PDF, para que los dos digan exactamente lo mismo.
   *
   * Población: todo lo que NO tiene CFDI. Quedan fuera las 'timbrada' (por
   * definición) y las 'historica', que son nóminas ya pagadas importadas del
   * sistema anterior — nunca se timbran aquí y serían ruido.
   *
   * El motivo se resuelve en cascada; ver `motivoDeLinea`.
   */
  async getDatosNoTimbrados() {
    const { prenomina, onValidacionParaReporte } = this.props
    const lineas = prenomina?.lineas || []

    // Enriquecimiento opcional: si falla, cada línea se queda con el motivo que
    // da su estatus. El reporte no depende de esto.
    let validacion = new Map()
    if (onValidacionParaReporte) {
      try { validacion = (await onValidacionParaReporte()) || new Map() } catch { /* degrada */ }
    }

    const ETIQUETA_ESTATUS = {
      pendiente:      'Pendiente (sin calcular)',
      calculada:      'Calculada',
      modificada:     'Modificada',
      error_timbrado: 'Rechazada por el PAC',
      sin_timbre:     'Sin timbre (recibo en ceros)',
    }

    const motivoDeLinea = (l) => {
      const v = validacion.get(String(l._id))
      const hallazgos = (v?.errores || []).map(e => e.mensaje).filter(Boolean)

      // 1. Rechazo del PAC. Se devuelven los dos textos: el código
      //    ("CFDI40999 - Error no clasificado") no dice qué corregir sin el
      //    detalle. Ojo: cuando el rechazo lo puso el guard local de sueldo, el
      //    detalle viene vacío y el motivo completo está en errorTimbrado — un
      //    detalle vacío NO significa "sin motivo".
      if (l.estatus === 'error_timbrado' && l.errorTimbrado) {
        return {
          motivo: l.errorTimbrado,
          detalle: [l.errorTimbradoDetalle, ...hallazgos].filter(Boolean).join(' · '),
        }
      }
      // 2. Recibo en ceros. Va ANTES de los hallazgos de la validación local: esta
      //    línea no está pendiente de nada —ya se decidió que no lleva CFDI— y sacar
      //    en su lugar un dato faltante del empleado la haría ver como accionable.
      if (l.estatus === 'sin_timbre') {
        return {
          motivo: 'Sin percepciones en el periodo, no requiere CFDI',
          detalle: 'No se mandó al PAC: un comprobante que no ampara nada lo rechaza el SAT.',
        }
      }
      // 3. Hallazgos de la validación local.
      if (hallazgos.length) {
        return { motivo: hallazgos[0], detalle: hallazgos.slice(1).join(' · ') }
      }
      // 4 y 5. Por estatus.
      if (l.estatus === 'pendiente') {
        return { motivo: 'No se ha calculado', detalle: '' }
      }
      return { motivo: 'Lista para timbrar, no se ha intentado', detalle: '' }
    }

    const filas = lineas
      .filter(l => l.estatus !== 'timbrada' && l.estatus !== 'historica')
      .map(l => {
        const { motivo, detalle } = motivoDeLinea(l)
        return {
          codigo: (l.codigo === null || l.codigo === undefined || l.codigo === '')
            ? '' : String(l.codigo).padStart(4, '0'),
          nombre: `${l.apellidoPaterno || ''} ${l.apellidoMaterno || ''} ${l.nombre || ''}`.trim(),
          rfc: l.rfc || '',
          departamento: l.departamentoNombre || '',
          estatus: ETIQUETA_ESTATUS[l.estatus] || l.estatus || '',
          motivo,
          detalle,
          neto: this.getNetoLocal(l),
        }
      })
      // Los rechazados primero: son los accionables de inmediato.
      .sort((a, b) => {
        const pa = a.estatus === ETIQUETA_ESTATUS.error_timbrado ? 0 : 1
        const pb = b.estatus === ETIQUETA_ESTATUS.error_timbrado ? 0 : 1
        return pa - pb || a.codigo.localeCompare(b.codigo)
      })

    return {
      filas,
      totalesNomina: this.getTotalesNomina(lineas),
      netoNoTimbrado: filas.reduce((s, f) => s + f.neto, 0),
      huboValidacion: validacion.size > 0,
    }
  }

  getDias() {
    const { fechaInicio, fechaFin } = this.props.prenomina || {}
    if (!fechaInicio || !fechaFin) return []
    const inicio = dayjs(fechaInicio)
    const fin    = dayjs(fechaFin)
    const n      = fin.diff(inicio, 'day') + 1
    if (n <= 0) return []
    return Array.from({ length: n }, (_, i) => i + 1)
  }

  getFecha(diaPeriodo) {
    const inicio = this.props.prenomina?.fechaInicio
    return inicio ? dayjs(inicio).add(diaPeriodo - 1, 'day') : null
  }

  getEntradasDia(linea, diaPeriodo) {
    const dia = (linea.incidencias || []).find(i => i.diaPeriodo === diaPeriodo)
    return dia?.entradas || []
  }

  /**
   * ¿Este día cae fuera de la relación laboral del empleado? La ventana la inyecta
   * datosPrenomina en cada línea (posInicioRelacion / posFinRelacion), calculada con el mismo
   * helper que usa el motor. Esos días no se pagan ni cotizan, así que la celda va de sólo
   * lectura: capturar ahí no descontaría nada.
   */
  /**
   * Posiciones (1-based) del día de descanso dentro del periodo, del snapshot de la Prenómina.
   * Mismo criterio y misma paleta que la rejilla del Sobre Recibo (IncidenciasTab): el motor
   * salta esas posiciones al descontar, así que una incidencia capturada ahí no baja el sueldo
   * ni el séptimo día —aunque sí cuenta para el ausentismo de IMSS, y por eso se marca en vez
   * de bloquearse.
   */
  getSeptimos() {
    return new Set((this.props.prenomina?.posisionSeptimoDia || []).map(Number))
  }

  fueraDeRelacion(linea, diaPeriodo) {
    const posInicio = Number(linea?.posInicioRelacion) || 1
    const posFin = Number.isFinite(Number(linea?.posFinRelacion))
      ? Number(linea.posFinRelacion)
      : Infinity
    return diaPeriodo < posInicio || diaPeriodo > posFin
  }

  // ── Modal de incidencias ──────────────────────────────────────────────────

  abrirModalInc(linea, diaPeriodo, fecha) {
    const tiposInc = this.getTiposInc()
    const entradas = this.getEntradasDia(linea, diaPeriodo)
    const idATipo = {}
    for (const t of tiposInc) if (t.id) idATipo[t.id] = t.tipo

    const cantidades = {}
    for (const t of tiposInc) cantidades[t.tipo] = 0
    for (const e of entradas) {
      const k = idATipo[e.tipoIncidenciaId] ?? e.tipo
      if (k !== undefined && cantidades[k] !== undefined) cantidades[k] = e.cantidad
    }

    const titulo = `${linea.apellidoPaterno} ${linea.nombre} — Día ${diaPeriodo}${fecha ? ' · ' + fecha.format('ddd D MMM') : ''}`
    this.setState({
      modalInc: { empleadoId: linea.empleadoId, diaPeriodo, fecha: fecha?.toISOString() ?? null, titulo },
      modalIncCantidades: cantidades,
      modalIncTexto: '',
      modalIncErrores: [],
    })
  }

  parsearTexto(texto) {
    const tiposInc = this.getTiposInc()
    const errores = []
    const cantidades = {}
    for (const t of tiposInc) cantidades[t.tipo] = 0

    const tokens = texto.trim().toUpperCase().split(/[\s,]+/).filter(Boolean)
    for (const token of tokens) {
      const m = token.match(/^(\d+)([A-Z_]+)$/)
      if (!m) { errores.push(`"${token}" — formato inválido (ej: 2RET)`); continue }
      const cantidad = parseInt(m[1], 10)
      const codigo  = m[2]
      const t = tiposInc.find(x => x.tipo && x.tipo.toUpperCase() === codigo)
      if (!t) { errores.push(`"${codigo}" no existe en el catálogo`); continue }
      cantidades[t.tipo] = cantidad
    }
    return { cantidades, errores }
  }

  aplicarTexto() {
    const { cantidades, errores } = this.parsearTexto(this.state.modalIncTexto)
    if (errores.length) {
      this.setState({ modalIncErrores: errores })
    } else {
      this.setState({ modalIncCantidades: cantidades, modalIncErrores: [] })
    }
  }

  async guardarModalInc() {
    const { modalInc, modalIncCantidades } = this.state
    const tiposInc = this.getTiposInc()
    const entradas = tiposInc
      .filter(t => (modalIncCantidades[t.tipo] || 0) > 0)
      .map(t => ({ tipo: t.tipo, tipoIncidenciaId: t.id, cantidad: modalIncCantidades[t.tipo], descripcion: '' }))

    await this.props.onGuardarIncidenciaDia(
      modalInc.empleadoId, modalInc.diaPeriodo, modalInc.fecha, entradas
    )
    this.setState({ modalInc: null })
  }

  renderModalInc() {
    const { modalInc, modalIncCantidades, modalIncTexto, modalIncErrores } = this.state
    if (!modalInc) return null
    const tiposInc = this.getTiposInc()

    return (
      <Modal
        visible={!!modalInc}
        title={<span style={{ fontSize: 13, fontWeight: 600 }}>{modalInc.titulo}</span>}
        onCancel={() => this.setState({ modalInc: null })}
        onOk={this.guardarModalInc.bind(this)}
        okText="Guardar"
        cancelText="Cancelar"
        width={420}
        destroyOnClose={true}
      >
        {/* Entrada rápida por clave */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>
            Escribe cantidad + clave (ej: <b>2RET</b> · <b>1FAL</b>) separadas por espacio y presiona Enter
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              autoFocus
              value={modalIncTexto}
              onChange={e => this.setState({ modalIncTexto: e.target.value, modalIncErrores: [] })}
              onKeyDown={e => { if (e.key === 'Enter') this.aplicarTexto() }}
              placeholder="Ej: 2RET 1FAL"
              style={{
                flex: 1, border: modalIncErrores.length ? '1px solid #ff4d4f' : '1px solid #d9d9d9',
                borderRadius: 6, padding: '7px 10px', fontSize: 13, outline: 'none',
              }}
            />
            <button
              onClick={this.aplicarTexto.bind(this)}
              style={{
                background: '#1677ff', color: '#fff', border: 'none',
                borderRadius: 6, padding: '0 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600,
              }}
            >
              Aplicar
            </button>
          </div>
          {modalIncErrores.length > 0 && (
            <div style={{ marginTop: 6 }}>
              {modalIncErrores.map((err, i) => (
                <div key={i} style={{ color: '#ff4d4f', fontSize: 11 }}>• {err}</div>
              ))}
            </div>
          )}
        </div>

        <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 14 }}>
          {tiposInc.length === 0 && (
            <div style={{ color: '#aaa', fontSize: 12, textAlign: 'center', padding: 16 }}>
              No hay tipos de incidencia configurados.
            </div>
          )}
          {tiposInc.map(t => {
            const val = modalIncCantidades[t.tipo] || 0
            return (
              <div key={t.tipo} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 10px', borderRadius: 6, marginBottom: 6,
                background: val > 0 ? `${t.color}14` : '#fafafa',
                border: val > 0 ? `1px solid ${t.color}50` : '1px solid #f0f0f0',
              }}>
                <Tag color={t.color} style={{ minWidth: 46, textAlign: 'center', fontFamily: 'monospace', fontSize: 11, margin: 0 }}>
                  {t.tipo}
                </Tag>
                <span style={{ flex: 1, fontSize: 13, color: val > 0 ? t.color : '#555', fontWeight: val > 0 ? 600 : 400 }}>
                  {t.label}
                </span>
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={val || ''}
                  placeholder="0"
                  style={{
                    width: 60, textAlign: 'center',
                    border: `1px solid ${val > 0 ? t.color : '#d0d0d0'}`,
                    borderRadius: 4, padding: '4px 6px', fontSize: 14, fontWeight: 700,
                    color: val > 0 ? t.color : '#bbb', outline: 'none', background: 'white',
                  }}
                  onChange={e => {
                    const v = parseInt(e.target.value, 10)
                    this.setState(prev => ({
                      modalIncCantidades: { ...prev.modalIncCantidades, [t.tipo]: isNaN(v) || v < 0 ? 0 : v },
                    }))
                  }}
                />
              </div>
            )
          })}
        </div>
      </Modal>
    )
  }

  // ── Logo de BIWO dentro del .xlsx ─────────────────────────────────────────
  // La implementación vive en listaRayaExcel.js, junto al Excel que la usa. Aquí
  // se deja el envoltorio porque el reporte de no timbrados arma su .xlsx con el
  // mismo mecanismo y sigue llamando a estos dos métodos.
  async cargarLogo() {
    return cargarLogoBiwo()
  }

  /**
   * Mete el logo en cada hoja del .xlsx ya generado y devuelve el Blob final.
   * @param {ArrayBuffer} buffer  el xlsx que escribió xlsx-js-style
   * @param {Uint8Array}  png     bytes del logo
   * @param {number}      hojas   cuántas hojas llevan logo (1..n, en orden)
   */
  async inyectarLogo(buffer, png, hojas) {
    return inyectarLogoEnHojas(buffer, png, hojas)
  }

  // ── Exportar Lista de Raya ────────────────────────────────────────────────
  /**
   * Excel de Lista de Raya de ESTA prenómina.
   *
   * El armado completo —paleta, encabezado, catálogo de conceptos, agrupamiento y
   * hojas— vive en `../listaRayaExcel`, compartido con el reporte de Reportes de
   * Nómina que saca la lista de varias prenóminas a la vez. Aquí sólo se le pasa la
   * prenómina que está en pantalla.
   *
   *  · `porProyecto = false` (por omisión): agrupa Registro Patronal → Departamento
   *    y emite dos hojas, `Lista de Raya` y `Concentrado`.
   *  · `porProyecto = true`: una hoja por proyecto más un `Concentrado`.
   *  · `filtroFiniquito`: `'todos'` la prenómina completa · `'sin'` deja fuera las
   *    líneas con finiquito aplicado, para entregar la raya del periodo sin que los
   *    importes de liquidación la inflen · `'solo'` deja únicamente las liquidaciones.
   *    El filtro corre antes del catálogo de conceptos, así que en cada variante sólo
   *    salen las columnas que de verdad tienen importe.
   *
   *  · `conIncidencias = true`: agrega al final del renglón una columna por día del
   *    periodo con lo capturado en la vista "Días e Incidencias", más el total de
   *    faltas, una columna de observaciones en blanco y las cuentas de banco. Es
   *    opt-in: el archivo de siempre no cambia de forma si no se pide, igual que
   *    `datosEmpleado` en el reporte multi-periodo.
   *
   * Los conceptos informativos (noSuma) se resuelven aquí, con getNoSumaSet(), porque
   * dependen del catálogo que trae el componente por props.
   */
  async descargarListaRaya(porProyecto = false, filtroFiniquito = 'todos', conIncidencias = false) {
    const { prenomina } = this.props
    const lineas = prenomina?.lineas || []

    if (filtroFiniquito === 'sin' && !lineas.some((l) => !l.finiquitoId)) {
      return message.info('Todos los empleados de esta nómina tienen finiquito: sin ellos no queda nada que exportar')
    }
    if (filtroFiniquito === 'solo' && !lineas.some((l) => l.finiquitoId)) {
      return message.info('Esta nómina no tiene ninguna línea de finiquito')
    }

    let incidencias = null
    let columnasBanco = []
    if (conIncidencias) {
      const dias = construirDiasPeriodo(prenomina)
      if (!dias.length) {
        return message.info('Esta nómina no tiene fechas de periodo, así que no se puede armar la rejilla de incidencias')
      }
      // Una columna por día: en un periodo razonable son 7, 15 o 31. Más que eso es un periodo
      // mal capturado, y la hoja saldría ilegible en vez de útil.
      if (dias.length > MAX_DIAS_REJILLA_EXCEL) {
        return message.info(`El periodo tiene ${dias.length} días y la rejilla de incidencias sólo llega a ${MAX_DIAS_REJILLA_EXCEL}`)
      }

      // El catálogo ya viene por props (PreNomina lo carga junto con la prenómina), con la
      // `cubeta` de cada tipo ya resuelta por el backend. Aquí sólo se indexa.
      const indice = construirIndiceTipos(this.props.tiposIncidencias || [])
      incidencias = { indice, observaciones: true }

      // Las cuentas de banco entran por el contrato que ya existe, `columnasExtra`: se insertan
      // después de "Empleado" y se pintan con numFmt '@', que es justo lo que necesita un número
      // de cuenta para no perder los ceros de la izquierda.
      const bancosPorId = await this.cargarBancosPorId()
      columnasBanco = [
        {
          titulo: 'Banco',
          wch: 18,
          valor: (f) => {
            const id = f.linea?.bancoPagoElectronicoId
            const banco = id ? bancosPorId[id.toString()] : null
            return banco ? (banco.nombre || banco.banco || '') : ''
          },
        },
        {
          titulo: 'Cuenta',
          wch: 20,
          valor: (f) => (f.linea?.numCuenta || f.linea?.clabeInterbancaria || '').trim(),
        },
      ]
    }

    try {
      return await descargarListaRayaExcel({
        prenominas: [prenomina],
        noSumaIds: this.getNoSumaSet(),
        especieIds: this.getEspecieSet(),
        opciones: { porProyecto, filtroFiniquito, incidencias, columnasExtra: columnasBanco },
      })
    } catch (err) {
      console.error('[descargarListaRaya]', err)
      message.error(err.message || 'No se pudo generar la Lista de Raya')
    }
  }

  /**
   * Catálogo de bancos indexado por id, para poner el nombre del banco pagador.
   *
   * Se pide al dar clic y no al abrir la prenómina, igual que en la dispersión bancaria — y con
   * su propio try, porque sin él las columnas salen en blanco pero el archivo se genera igual.
   */
  async cargarBancosPorId() {
    try {
      const res = await axios.post(
        'bancos/selectBancos',
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken },
      )
      const bancos = res.data?.bancos || (Array.isArray(res.data) ? res.data : [])
      return Object.fromEntries(
        bancos.map((b) => [b._id.toString(), { nombre: b.nombre, banco: b.banco }]),
      )
    } catch (e) {
      console.error('[listaRaya] no se pudo cargar el catálogo de bancos', e)
      return {}
    }
  }

  /**
   * Excel plano "Datos por empleado": un renglón por empleado con los días no pagados (faltas,
   * incapacidades, fuera de la relación laboral), la baja, la cuenta y cada concepto en su columna.
   * Lo lee la Calculadora de Asimilables que el cliente lleva FUERA del sistema; ver
   * `construirLibroDatosPorEmpleado` en listaRayaExcel.js. Los finiquitos no salen.
   */
  async descargarDatosPorEmpleado() {
    const { prenomina } = this.props
    const lineas = prenomina?.lineas || []
    if (!lineas.length) return message.info('La nómina no tiene empleados')
    if (!lineas.some((l) => !l.finiquitoId)) {
      return message.info('Todos los empleados de esta nómina son finiquitos, y los finiquitos no van en esta descarga')
    }

    const dias = construirDiasPeriodo(prenomina)
    if (!dias.length) {
      return message.info('Esta nómina no tiene fechas de periodo, así que no se pueden contar los días no pagados')
    }
    if (dias.length > MAX_DIAS_REJILLA_EXCEL) {
      return message.info(`El periodo tiene ${dias.length} días; el conteo de días sólo llega a ${MAX_DIAS_REJILLA_EXCEL}`)
    }

    try {
      const bancosPorId = await this.cargarBancosPorId()
      descargarDatosPorEmpleadoExcel({
        prenomina,
        noSumaIds: this.getNoSumaSet(),
        especieIds: this.getEspecieSet(),
        indice: construirIndiceTipos(this.props.tiposIncidencias || []),
        bancosPorId,
      })
    } catch (err) {
      console.error('[descargarDatosPorEmpleado]', err)
      message.error(err.message || 'No se pudo generar el Excel de datos por empleado')
    }
  }

  // ── Exportar dispersión bancaria ──────────────────────────────────────────
  /**
   * Layout para subir la nómina al portal del banco: una hoja por banco pagador
   * (`Empleado.bancoPagoElectronicoId` apunta a la cuenta de la EMPRESA con la que se
   * le paga) y una hoja de efectivo con los que no tienen cuenta capturada.
   *
   * La cuenta, la CLABE y el banco ya vienen en la línea —`datosPrenomina` los
   * refresca del empleado— así que lo único que hay que pedir es el catálogo de
   * bancos para poner el nombre, y se pide aquí, al dar clic, en vez de cargarlo en
   * cada apertura de la prenómina.
   */
  async descargarDispersionBancaria() {
    const { prenomina } = this.props

    if (!prenomina?.lineas?.length) {
      return message.info('Esta nómina no tiene empleados')
    }

    this.setState({ descargandoDispersion: true })
    try {
      let bancosPorId = {}
      try {
        const res = await axios.post(
          'bancos/selectBancos',
          { empresaId: this.props.empresaId },
          { headers: this.props.headersToken },
        )
        const bancos = res.data?.bancos || (Array.isArray(res.data) ? res.data : [])
        bancosPorId = Object.fromEntries(
          bancos.map((b) => [b._id.toString(), { nombre: b.nombre, banco: b.banco }]),
        )
      } catch (e) {
        // Sin el catálogo las hojas salen igual, sólo que rotuladas "Sin banco":
        // los importes y las cuentas, que es lo que sube al portal, no dependen de él.
        console.error('[dispersion] no se pudo cargar el catálogo de bancos', e)
      }

      await descargarDispersionExcel({
        prenomina,
        bancosPorId,
        noSumaIds: this.getNoSumaSet(),
        especieIds: this.getEspecieSet(),
      })
    } catch (err) {
      console.error('[dispersion] no se pudo generar el archivo', err)
      message.error('No se pudo generar el archivo de dispersión')
    } finally {
      this.setState({ descargandoDispersion: false })
    }
  }

  // ── Exportar reporte de no timbrados ──────────────────────────────────────
  /**
   * Excel de los empleados que no se pudieron timbrar y por qué.
   *
   * Reusa el mismo pipeline visual de la Lista de Raya —paleta, tipografía,
   * encabezado con logo y la inyección del PNG en el zip— para que los dos
   * reportes de la pantalla se vean como el mismo producto.
   */
  async descargarReporteNoTimbradosExcel() {
    const { prenomina } = this.props
    const datos = await this.getDatosNoTimbrados()

    if (datos.filas.length === 0) {
      message.info('Todos los empleados de esta nómina están timbrados')
      return
    }

    const png = await this.cargarLogo()
    const conLogo = !!png

    const FORMATO_CONTABILIDAD = '_-"$"* #,##0.00_-;-"$"* #,##0.00_-;_-"$"* "-"??_-;_-@_-'
    const TURQUESA = '00BFD6', NARANJA = 'FF6A1A', TEAL_OSC = '007A8A', TEAL_PRO = '005F6B'
    const BANDA_GR = 'BFEDF5', ZEBRA = 'F5FBFC'
    const TINTA = '262626', TINTA_SU = '8C8C8C', ROJO = 'E53935'

    const ca = (ex = {}) => ({ name: 'Calibri', sz: 9, color: { rgb: TINTA }, ...ex })
    const sBiwo    = { font: ca({ sz: 20, bold: true, color: { rgb: TURQUESA } }), alignment: { horizontal: 'left', vertical: 'center' }, numFmt: '@' }
    const sNominas = { font: ca({ sz: 9, bold: true, color: { rgb: NARANJA } }), alignment: { horizontal: 'left', vertical: 'center' }, numFmt: '@' }
    const sEmpresa = { font: ca({ sz: 14, bold: true }), alignment: { horizontal: 'left', vertical: 'center' } }
    const sTitulo  = { font: ca({ sz: 11, color: { rgb: TINTA_SU } }), alignment: { horizontal: 'left', vertical: 'center' } }
    const sMeta    = { font: ca({ color: { rgb: TINTA_SU } }), alignment: { horizontal: 'left', vertical: 'center' } }
    const sRegla   = { fill: { fgColor: { rgb: TURQUESA } } }

    const hdr = (fondo = TURQUESA) => ({
      fill: { fgColor: { rgb: fondo } },
      font: ca({ bold: true, color: { rgb: 'FFFFFF' } }),
      border: { bottom: { style: 'medium', color: { rgb: TEAL_PRO } } },
      alignment: { horizontal: 'left', vertical: 'center', wrapText: true },
    })
    const relleno = (rgb) => rgb ? { fill: { fgColor: { rgb } } } : {}
    const sTxt = (impar) => ({ font: ca(), ...relleno(impar ? ZEBRA : null), alignment: { horizontal: 'left', vertical: 'top', wrapText: true }, numFmt: '@' })
    // El motivo en rojo: es el dato por el que se abre este reporte.
    const sMotivo = (impar) => ({ font: ca({ color: { rgb: ROJO } }), ...relleno(impar ? ZEBRA : null), alignment: { horizontal: 'left', vertical: 'top', wrapText: true }, numFmt: '@' })
    const sImp = (impar) => ({ font: ca(), ...relleno(impar ? ZEBRA : null), alignment: { horizontal: 'right', vertical: 'top' }, numFmt: FORMATO_CONTABILIDAD })

    const bordeGT = { top: { style: 'medium', color: { rgb: TEAL_OSC } } }
    const sGTLbl  = { fill: { fgColor: { rgb: BANDA_GR } }, font: ca({ sz: 10, bold: true, color: { rgb: TEAL_PRO } }), border: bordeGT, alignment: { horizontal: 'left' }, numFmt: '@' }
    const sGTNum  = { fill: { fgColor: { rgb: BANDA_GR } }, font: ca({ sz: 10, bold: true, color: { rgb: TEAL_PRO } }), border: bordeGT, alignment: { horizontal: 'right' }, numFmt: FORMATO_CONTABILIDAD }

    const cs = (v, s) => ({ v: v || '', t: 's', s })
    const cn = (v, s) => ({ v: typeof v === 'number' ? v : 0, t: 'n', s })
    const ce = (s) => ({ v: '', t: 's', s })

    const N_COLS = 8
    const ULT = N_COLS - 1
    const filas = [], merges = []

    const fi = prenomina.fechaInicio ? dayjs(prenomina.fechaInicio).format('DD/MM/YYYY') : ''
    const ff = prenomina.fechaFin ? dayjs(prenomina.fechaFin).format('DD/MM/YYYY') : ''
    const periodoTxt = [
      prenomina.periodoNumero != null ? `Periodo ${prenomina.periodoNumero}` : 'Periodo',
      prenomina.tipoPeriodoNombre || '',
      fi && ff ? `· del ${fi} al ${ff}` : '',
    ].filter(Boolean).join(' ')

    const filaSoloAlto = () => [ce(sMeta)]
    if (conLogo) { filas.push(filaSoloAlto()); filas.push(filaSoloAlto()) }
    else { filas.push([cs('BIWO', sBiwo)]); filas.push([cs('NÓMINAS', sNominas)]) }
    filas.push(filaSoloAlto())
    filas.push([cs(prenomina.empresaNombre || '', sEmpresa)])
    filas.push([cs('Empleados no timbrados', sTitulo)])
    filas.push([cs(periodoTxt, sMeta)])
    filas.push([cs(
      `${datos.filas.length} sin timbrar de ${datos.totalesNomina.empleados} empleado(s)` +
      (datos.huboValidacion ? '' : '  ·  sin diagnóstico de validación'),
      sMeta
    )])
    for (let r = 3; r <= 6; r++) merges.push({ s: { r, c: 0 }, e: { r, c: ULT } })
    filas.push(Array.from({ length: N_COLS }, () => ce(sRegla)))
    filas.push([])

    filas.push([
      cs('Código', hdr()), cs('Empleado', hdr()), cs('RFC', hdr()),
      cs('Departamento', hdr()), cs('Estatus', hdr()),
      cs('Motivo', hdr(TEAL_OSC)), cs('Detalle', hdr(TEAL_OSC)),
      cs('Neto', hdr(TEAL_OSC)),
    ])

    datos.filas.forEach((f, i) => {
      const impar = i % 2 === 1
      filas.push([
        cs(f.codigo, sTxt(impar)), cs(f.nombre, sTxt(impar)), cs(f.rfc, sTxt(impar)),
        cs(f.departamento, sTxt(impar)), cs(f.estatus, sTxt(impar)),
        cs(f.motivo, sMotivo(impar)), cs(f.detalle, sTxt(impar)),
        cn(f.neto, sImp(impar)),
      ])
    })

    // Sólo la suma de lo que aparece en este reporte. Los totales de TODA la
    // nómina se quedan fuera a propósito: este documento se comparte con quien va
    // a corregir los datos, y no tiene por qué llevar lo que cobró el resto de la
    // empresa. En pantalla sí están, para quien opera la nómina.
    const fNoTimb = filas.length
    filas.push([
      cs('NETO PENDIENTE DE TIMBRAR', sGTLbl),
      ...Array.from({ length: 6 }, () => ce(sGTLbl)),
      cn(datos.netoNoTimbrado, sGTNum),
    ])
    merges.push({ s: { r: fNoTimb, c: 0 }, e: { r: fNoTimb, c: 6 } })

    const ws = XLSX.utils.aoa_to_sheet(filas)
    ws['!merges'] = merges
    ws['!rows'] = [
      { hpt: 26 }, { hpt: 26 }, { hpt: 8 },
      { hpt: 19 }, { hpt: 15 }, { hpt: 14 }, { hpt: 14 },
      { hpt: 4 }, { hpt: 7 }, { hpt: 30 },
    ]
    ws['!cols'] = [
      { wch: 10 }, { wch: 32 }, { wch: 15 }, { wch: 22 },
      { wch: 22 }, { wch: 52 }, { wch: 52 }, { wch: 15 },
    ]

    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'No timbrados')

    const nombreArchivo = `no_timbrados_${(prenomina.nombre || 'nomina').replace(/[/\\?*[\]:#]/g, '_')}.xlsx`
    if (!conLogo) return XLSX.writeFile(wb, nombreArchivo)

    const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
    try {
      saveAs(await this.inyectarLogo(buffer, png, wb.SheetNames.length), nombreArchivo)
    } catch (err) {
      console.error('[reporteNoTimbrados] no se pudo insertar el logo', err)
      XLSX.writeFile(wb, nombreArchivo)
    }
  }

  /** Mismo reporte en PDF. El docDefinition vive en src/Pdf, como los demás. */
  async descargarReporteNoTimbradosPdf() {
    const { prenomina } = this.props
    const datos = await this.getDatosNoTimbrados()

    if (datos.filas.length === 0) {
      message.info('Todos los empleados de esta nómina están timbrados')
      return
    }

    const definicion = nominaNoTimbradosPdf({ prenomina, ...datos })
    pdfMake.createPdf(definicion).download(
      `no_timbrados_${(prenomina.nombre || 'nomina').replace(/[/\\?*[\]:#]/g, '_')}.pdf`
    )
  }

  // ── Barra de tabs y acciones ──────────────────────────────────────────────
  renderBarra() {
    const { calculando, onCalcularTodo, onAbrirColumnas, onAgregarEmpleado, onResetearLineas, onLimpiarIncidencias, onDescargarRecibos, onVerRecibos, onEnviarRecibos, prenomina, soloLectura, incidenciasSoloLectura } = this.props
    const { vistaActiva, filtroEmpleado } = this.state
    const totalLineas   = prenomina.lineas.length
    const visiblesCount = this.getLineasFiltradas().length
    const hayFiltro     = (filtroEmpleado || '').trim() !== ''
    const totalesNomina = this.getTotalesNomina(prenomina.lineas)
    // Con finiquitos en la prenómina el chip se abre en tres bloques: la nómina ordinaria por
    // un lado, las liquidaciones por otro y el desembolso total. Un finiquito puede ser varias
    // veces el sueldo de un mes, y mezclado en una sola cifra hace ilegible el total del periodo.
    const lineasFiniquito = (prenomina.lineas || []).filter(l => l.finiquitoId)
    const totalesFiniquito = lineasFiniquito.length
      ? this.getTotalesNomina(lineasFiniquito)
      : null
    const totalesOrdinarios = totalesFiniquito
      ? this.getTotalesNomina((prenomina.lineas || []).filter(l => !l.finiquitoId))
      : null

    return (
      <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>

        {/* Tabs de vista */}
        <div style={{ display: 'flex', border: '1px solid #d0d0d0', borderRadius: 6, overflow: 'hidden' }}>
          {[
            { key: 'importes',    label: 'Importes',          icon: <TableOutlined />    },
            { key: 'incidencias', label: 'Días e Incidencias', icon: <CalendarOutlined /> },
          ].map(({ key, label, icon }) => (
            <button
              key={key}
              onClick={() => this.setState({ vistaActiva: key })}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '4px 12px', border: 'none', cursor: 'pointer', fontSize: 12,
                background: vistaActiva === key ? '#1677ff' : '#fff',
                color:      vistaActiva === key ? '#fff'    : '#555',
                fontWeight: vistaActiva === key ? 600       : 400,
                transition: 'all .15s',
              }}
            >
              {icon} {label}
            </button>
          ))}
        </div>

        <Input
          prefix={<SearchOutlined style={{ color: '#bbb' }} />}
          placeholder="Buscar por código, nombre o RFC..."
          value={filtroEmpleado}
          onChange={e => this.setState({ filtroEmpleado: e.target.value })}
          allowClear
          size="small"
          style={{ width: 230 }}
        />

        <span style={{ fontSize: 12, color: '#888' }}>
          {hayFiltro
            ? `${visiblesCount} de ${totalLineas} empleado(s)`
            : `${totalLineas} empleado(s)`}
        </span>

        <span style={{ flex: 1 }} />

        {/* Acciones */}
        {!soloLectura && (
          <ButtonItem azul size="small" icon={<PlusOutlined />} onClick={onAgregarEmpleado}>
            Agregar empleado
          </ButtonItem>
        )}
        {/* Va junto a "Agregar empleado" y no en el PageHeader: el header es el ciclo de vida del
            documento (cerrar / timbrar / cancelar) y el padrón de la tabla se administra desde
            aquí. En una histórica no aplica — su padrón es el del periodo que carga, no el de hoy. */}
        {!soloLectura && !prenomina.esHistorica && (
          <ButtonItem naranja size="small" icon={<SyncOutlined />} onClick={onResetearLineas}>
            Resetear líneas
          </ButtonItem>
        )}
        {vistaActiva === 'importes' && (
          <ButtonItem gris size="small" icon={<SettingOutlined />} onClick={onAbrirColumnas}>
            Columnas
          </ButtonItem>
        )}
        {/* Preferencia de la empresa "Incidencias sólo para consulta": sin esta marca, una
            rejilla que no deja escribir y un menú que desapareció parecen una falla. */}
        {vistaActiva === 'incidencias' && incidenciasSoloLectura && (
          <Tooltip title="La empresa configuró las incidencias sólo para consulta (Configuración de Nómina › Preferencias de PreNómina). No se capturan, no se borran ni se importan desde aquí.">
            <Tag color="default" style={{ margin: 0 }}>Incidencias: sólo consulta</Tag>
          </Tooltip>
        )}
        {/* Las tres acciones masivas de la rejilla en un solo menú. Eran dos botones sueltos y
            se juntaron al agregar el borrado: tres botones más en una barra que ya se desborda
            era justo lo que el comentario de abajo pide no hacer. */}
        {vistaActiva === 'incidencias' && !soloLectura && !incidenciasSoloLectura && (
          <Dropdown
            trigger={['click']}
            overlay={(
              <Menu>
                <Menu.Item key="asistencia" icon={<ImportOutlined />}
                  onClick={() => this.setState({ modalImportarAsistenciaVisible: true })}>
                  Importar de asistencia
                </Menu.Item>
                <Menu.Item key="excel" icon={<FileExcelOutlined />}
                  onClick={() => this.setState({ modalImportarExcelVisible: true })}>
                  Importar de Excel
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item key="limpiar" danger icon={<DeleteOutlined />}
                  onClick={onLimpiarIncidencias}>
                  Borrar todas las incidencias
                </Menu.Item>
              </Menu>
            )}
          >
            <ButtonItem morado size="small" icon={<CalendarOutlined />}>
              Incidencias <DownOutlined />
            </ButtonItem>
          </Dropdown>
        )}
        {/* Tres menús y no diez botones: la barra ya venía desbordada (el de "No
            timbrados" existía por eso mismo) y con el buscador, las pestañas y el
            chip de totales no cabía nada más. Lo que se queda suelto es lo que se
            usa a cada rato; lo demás está a un clic. */}
        <Dropdown
          trigger={['click']}
          overlay={lineasFiniquito.length ? (
            // Con finiquitos en la prenómina, cada acción se abre en las tres variantes de la
            // Lista de Raya: la ordinaria primero y resaltada, porque lo de rutina es imprimir la
            // nómina del periodo sin las liquidaciones revueltas. Sin finiquitos, el menú de abajo.
            <Menu>
              {[
                { key: 'ver', icon: <PrinterOutlined />, titulo: 'Ver e imprimir',
                  // Mismo getLineasFiltradas() que el menú sin finiquitos: respeta el buscador.
                  accion: (filtro) => onVerRecibos(this.getLineasFiltradas(), filtro) },
                { key: 'descargar', icon: <FilePdfOutlined />, titulo: 'Descargar recibos',
                  accion: (filtro) => onDescargarRecibos(filtro) },
                { key: 'enviar', icon: <MailOutlined />, titulo: 'Enviar por correo',
                  accion: (filtro) => onEnviarRecibos(filtro) },
              ].map(({ key, icon, titulo, accion }) => (
                <Menu.SubMenu key={key} icon={icon} title={titulo}>
                  {OPCIONES_FINIQUITO.map(o => (
                    <Menu.Item key={`${key}-${o.valor}`}
                      style={o.destacada ? ESTILO_OPCION_DESTACADA : undefined}
                      onClick={() => accion(o.valor)}>
                      {o.etiqueta}
                    </Menu.Item>
                  ))}
                </Menu.SubMenu>
              ))}
            </Menu>
          ) : (
            <Menu>
              {/* Se manda getLineasFiltradas(), no prenomina.lineas: si hay algo
                  escrito en el buscador, el visor imprime sólo eso. */}
              <Menu.Item key="ver" icon={<PrinterOutlined />}
                onClick={() => onVerRecibos(this.getLineasFiltradas())}>
                Ver e imprimir
              </Menu.Item>
              {/* En flecha: antd le pasa al onClick el evento del menú, y los dos handlers
                  ahora leen su primer argumento como el filtro de finiquitos. */}
              <Menu.Item key="descargar" icon={<FilePdfOutlined />} onClick={() => onDescargarRecibos()}>
                Descargar recibos
              </Menu.Item>
              <Menu.Item key="enviar" icon={<MailOutlined />} onClick={() => onEnviarRecibos()}>
                Enviar por correo
              </Menu.Item>
            </Menu>
          )}
        >
          <ButtonItem morado size="small" icon={<FilePdfOutlined />}>
            Recibos <DownOutlined />
          </ButtonItem>
        </Dropdown>
        <Dropdown
          trigger={['click']}
          overlay={(
            <Menu>
              {/* Dos familias (normal y por proyecto), cada una con las tres variantes. La
                  ORDINARIA va primera y resaltada porque es la que se entrega de rutina: es la
                  raya del periodo sin que las liquidaciones la inflen. La completa y la de sólo
                  finiquitos quedan debajo, para cuadrar o para revisar una liquidación. */}
              <Menu.SubMenu key="raya" icon={<DownloadOutlined />} title="Lista de Raya">
                <Menu.Item key="raya-sin" style={ESTILO_OPCION_DESTACADA}
                  onClick={() => this.descargarListaRaya(false, 'sin')}>
                  Ordinaria (sin finiquitos)
                </Menu.Item>
                <Menu.Item key="raya-todos" onClick={() => this.descargarListaRaya(false, 'todos')}>
                  Completa (con finiquitos)
                </Menu.Item>
                <Menu.Item key="raya-solo" onClick={() => this.descargarListaRaya(false, 'solo')}>
                  Sólo finiquitos
                </Menu.Item>
              </Menu.SubMenu>
              <Menu.SubMenu key="raya-proyecto" icon={<DownloadOutlined />} title="Lista de Raya x Proyecto">
                <Menu.Item key="raya-proyecto-sin" style={ESTILO_OPCION_DESTACADA}
                  onClick={() => this.descargarListaRaya(true, 'sin')}>
                  Ordinaria (sin finiquitos)
                </Menu.Item>
                <Menu.Item key="raya-proyecto-todos" onClick={() => this.descargarListaRaya(true, 'todos')}>
                  Completa (con finiquitos)
                </Menu.Item>
                <Menu.Item key="raya-proyecto-solo" onClick={() => this.descargarListaRaya(true, 'solo')}>
                  Sólo finiquitos
                </Menu.Item>
              </Menu.SubMenu>
              {/* Tercera familia: la misma raya con una columna por día del periodo, tomada de lo
                  capturado en "Días e Incidencias", más el total de faltas, las cuentas de banco y
                  una columna de observaciones en blanco. Va aparte y no como opción de las dos de
                  arriba para que el archivo de siempre no cambie de forma a quien ya lo usa. */}
              <Menu.SubMenu key="raya-incidencias" icon={<CalendarOutlined />} title="Lista de Raya con Incidencias">
                <Menu.Item key="raya-incidencias-sin" style={ESTILO_OPCION_DESTACADA}
                  onClick={() => this.descargarListaRaya(false, 'sin', true)}>
                  Ordinaria (sin finiquitos)
                </Menu.Item>
                <Menu.Item key="raya-incidencias-todos" onClick={() => this.descargarListaRaya(false, 'todos', true)}>
                  Completa (con finiquitos)
                </Menu.Item>
                <Menu.Item key="raya-incidencias-solo" onClick={() => this.descargarListaRaya(false, 'solo', true)}>
                  Sólo finiquitos
                </Menu.Item>
              </Menu.SubMenu>
              {/* Hoja plana para cálculos fuera del sistema (la Calculadora de Asimilables del
                  cliente): encabezados fijos en la fila 1 y un renglón por empleado, SIN finiquitos
                  y con las bajas marcadas. Sin logo ni totales porque la lee una macro. */}
              <Menu.Item key="datos-por-empleado" icon={<TableOutlined />}
                onClick={() => this.descargarDatosPorEmpleado()}>
                Datos por empleado (Excel plano)
              </Menu.Item>
              <Menu.Item key="dispersion" icon={<BankOutlined />}
                disabled={this.state.descargandoDispersion}
                onClick={() => this.descargarDispersionBancaria()}>
                Dispersión bancaria
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item key="no-timbrados-excel" icon={<FileExcelOutlined />}
                onClick={() => this.descargarReporteNoTimbradosExcel()}>
                No timbrados — Excel
              </Menu.Item>
              <Menu.Item key="no-timbrados-pdf" icon={<WarningOutlined />}
                onClick={() => this.descargarReporteNoTimbradosPdf()}>
                No timbrados — PDF
              </Menu.Item>
            </Menu>
          )}
        >
          <ButtonItem celeste size="small" icon={<DownloadOutlined />}>
            Descargas <DownOutlined />
          </ButtonItem>
        </Dropdown>
        {!soloLectura && (
          <ButtonItem
            lima size="small"
            icon={<CalculatorOutlined />}
            loading={calculando}
            onClick={onCalcularTodo}
          >
            Calcular todo
          </ButtonItem>
        )}
      </div>

      {/* Totales de la nómina, en su propio renglón debajo de los botones: en la
          barra de arriba competían por el espacio con el buscador y las acciones,
          y con la pantalla angosta se iban de línea a media fila.

          Van sobre prenomina.lineas COMPLETO, nunca sobre lo filtrado: es el
          mismo criterio del pie de la tabla, para que un filtro de pantalla no se
          lea como el total de la nómina. */}
      <div style={{
        display: 'flex', justifyContent: 'flex-end', marginBottom: 12, gap: 8, flexWrap: 'wrap',
      }}>
        {totalesFiniquito ? (
          // Con finiquitos van los tres bloques en orden: primero la nómina ordinaria con su
          // desglose completo, luego el de las liquidaciones y al final el neto de todo, que es
          // el desembolso real del periodo.
          <>
            {renderChipTotales('Ordinaria', totalesOrdinarios, {
              fondo: '#FAFAFA', borde: '#E8E8E8', color: '#595959',
            })}
            {renderChipTotales('Finiquitos', totalesFiniquito, {
              fondo: '#F7F2FD', borde: '#E3D6F5', color: '#7a4fbf',
            })}
            {renderChipTotales('Total', totalesNomina, {
              fondo: '#F5FBFC', borde: '#DFF5F9', color: '#005F6B', resaltado: true, hayFiltro,
            })}
          </>
        ) : (
          renderChipTotales(null, totalesNomina, {
            fondo: '#F5FBFC', borde: '#DFF5F9', color: '#005F6B', hayFiltro,
          })
        )}
      </div>
      </>
    )
  }

  // ─────────────────────────────────────────────────────────────────────────
  // VISTA 1 — Tabla de Importes
  // ─────────────────────────────────────────────────────────────────────────
  /**
   * Un finiquito se paga DENTRO de la prenómina ordinaria (reusa la línea del empleado, ver
   * LineaPrenomina.finiquitoId), así que una liquidación de $230,000 se sumaba a la nómina del
   * periodo y distorsionaba el total sin que se pudiera leer por separado. Se pintan dos tablas:
   * la nómina normal arriba y los finiquitos abajo, cada una con sus totales, más un gran total
   * al pie con lo que realmente se paga en el periodo.
   *
   * Sin finiquitos en la prenómina esto devuelve UNA tabla, exactamente la de siempre.
   */
  renderTablaImportes() {
    const todas = this.props.prenomina.lineas || []
    const lineasVisibles = this.getLineasFiltradas()

    if (!todas.some(l => l.finiquitoId)) {
      return this.renderTablaDeImportes(lineasVisibles, { lineasTotales: todas })
    }

    const esFin = (l) => !!l.finiquitoId
    const totalesFiniquito = todas.filter(esFin)

    return (
      <>
        {this.renderTablaDeImportes(lineasVisibles.filter(l => !esFin(l)), {
          titulo: 'Nómina ordinaria',
          subtitulo: 'Sin las liquidaciones del periodo',
          lineasTotales: todas.filter(l => !esFin(l)),
          etiquetaTotales: 'TOTAL ORDINARIA',
        })}
        {/* Las dos tablas comparten `columnasVisibles`: es la configuración de columnas de la
            prenómina y vale para las dos, así los importes de un mismo concepto quedan alineados
            en la misma columna arriba y abajo. Lo que el finiquito trae de más (indemnización,
            prima de antigüedad, ISR de separación) cae en Otras Perc./Otras Dedu., igual que
            cualquier otro concepto fuera de las columnas elegidas; para verlo desglosado están
            el sobre-recibo, la Lista de Raya y la pantalla del finiquito. */}
        {this.renderTablaDeImportes(lineasVisibles.filter(esFin), {
          titulo: 'Finiquitos',
          subtitulo: 'Se timbran en esta misma prenómina, pero no forman parte de la nómina ordinaria',
          lineasTotales: totalesFiniquito,
          etiquetaTotales: 'TOTAL FINIQUITOS',
          acento: '#7a4fbf',
        })}
        {this.renderGranTotal(todas)}
      </>
    )
  }

  /** El desembolso real del periodo: nómina ordinaria + finiquitos. */
  renderGranTotal(todas) {
    const t = this.getTotalesNomina(todas)
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 18, fontSize: 13,
          background: '#005F6B', color: 'white', borderRadius: 6, padding: '8px 16px',
          whiteSpace: 'nowrap',
        }}>
          <span style={{ fontWeight: 600 }}>TOTAL PRENÓMINA</span>
          <span style={{ opacity: 0.75 }}>{t.empleados} empleados</span>
          <span style={{ opacity: 0.85 }}>Percepciones <b>{fmt(t.percepciones)}</b></span>
          <span style={{ opacity: 0.85 }}>Deducciones <b>{fmt(t.deducciones)}</b></span>
          <span style={{ fontWeight: 700, fontSize: 14 }}>Neto {fmt(t.neto)}</span>
        </div>
      </div>
    )
  }

  renderTablaDeImportes(lineasVisibles, opciones = {}) {
    const { prenomina, calculando, cerrando, cancelando, soloLectura, onCalcularLinea, onQuitarEmpleado, onAbrirSobreRecibo, onAbrirCfdi, onDescargarXml, onImprimirRecibo, onCancelarCfdi, onReintentarLinea, puedeTimbrarLineaHistorica, timbrandoLineaHistorica, onTimbrarLineaHistorica } = this.props
    const columnas  = opciones.columnas || this.getColumnas()
    const tieneColumnas = columnas.length > 0
    // Los totales del pie son SIEMPRE del conjunto completo de esta sección, nunca de lo
    // filtrado en pantalla: un total parcial se lee por error como el de la nómina.
    const lineasTotales = opciones.lineasTotales || prenomina.lineas

    // Cuántas sub-columnas dinámicas hay en total
    const subColsDinamicas = columnas.reduce(
      (acc, c) => acc + (c.mostrarValor && c.mostrarImporte ? 2 : 1), 0
    )

    return (
      <div style={{ overflowX: 'auto' }}>
        {opciones.titulo && renderTituloSeccion({
          titulo: opciones.titulo,
          subtitulo: opciones.subtitulo,
          cuenta: lineasTotales.length,
          acento: opciones.acento || '#005F6B',
        })}
        <table style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'auto' }}>
          <thead>

            {/* Fila de grupos — solo si hay columnas dinámicas */}
            {tieneColumnas && (
              <tr>
                {this.renderThCodigo({ rowSpan: 2 })}
                {this.renderThOrdenable('nombre', 'Empleado', {
                  estilo: { textAlign: 'left', minWidth: 200 }, rowSpan: 2,
                })}

                {columnas.map(col => (
                  <th
                    key={col.conceptoNominaId}
                    style={{ ...TH, minWidth: col.mostrarValor && col.mostrarImporte ? 160 : 90 }}
                    colSpan={col.mostrarValor && col.mostrarImporte ? 2 : 1}
                  >
                    <div style={{ fontSize: 10, color: '#888' }}>{col.def.serie}-{col.def.numero}</div>
                    <div style={{ fontSize: 11 }}>{col.def.descripcion}</div>
                    {col.def.noSuma && (
                      <div style={{ fontSize: 9, color: '#aaa', fontWeight: 400 }}>informativo</div>
                    )}
                  </th>
                ))}

                <th style={{ ...TH, minWidth: 100, background: '#f0f5ff' }} rowSpan={2}>Otras Perc.</th>
                <th style={{ ...TH, minWidth: 100, background: '#fff1f0' }} rowSpan={2}>Otras Dedu.</th>
                {this.renderThOrdenable('neto', 'Neto', { estilo: { minWidth: 90 }, rowSpan: 2 })}
                <th style={{ ...TH, width: 85 }}                           rowSpan={2}>Estatus</th>
                <th style={{ ...TH, width: 100 }}                          rowSpan={2}>Acciones</th>
              </tr>
            )}

            {/* Fila de encabezados — siempre visible */}
            <tr>
              {/* Si no hay columnas dinámicas, los encabezados de Cód. y Empleado van aquí */}
              {!tieneColumnas && [
                this.renderThCodigo({ llave: 'cod' }),
                this.renderThOrdenable('nombre', 'Empleado', {
                  estilo: { textAlign: 'left', minWidth: 200 }, llave: 'emp',
                }),
              ]}

              {/* Sub-encabezados Valor / Importe para columnas dinámicas */}
              {columnas.map(col => {
                if (col.mostrarValor && col.mostrarImporte) {
                  return [
                    <th key={`${col.conceptoNominaId}-v`} style={{ ...TH, fontSize: 10, width: 70 }}>Valor</th>,
                    <th key={`${col.conceptoNominaId}-i`} style={{ ...TH, fontSize: 10, width: 90 }}>Importe</th>,
                  ]
                }
                return (
                  <th key={`${col.conceptoNominaId}-solo`} style={{ ...TH, fontSize: 10, width: 90 }}>
                    {col.mostrarValor ? 'Valor' : 'Importe'}
                  </th>
                )
              })}

              {/* Si no hay columnas dinámicas, poner los fijos aquí también */}
              {!tieneColumnas && [
                <th key="op" style={{ ...TH, minWidth: 100, background: '#f0f5ff' }}>Otras Perc.</th>,
                <th key="od" style={{ ...TH, minWidth: 100, background: '#fff1f0' }}>Otras Dedu.</th>,
                this.renderThOrdenable('neto', 'Neto', { estilo: { minWidth: 90 }, llave: 'n' }),
                <th key="s"  style={{ ...TH, width: 85 }}>Estatus</th>,
                <th key="a"  style={{ ...TH, width: 100 }}>Acciones</th>,
              ]}
            </tr>
          </thead>

          <tbody>
            {lineasVisibles.map((linea, idx) => {
              const rowBg = idx % 2 === 0 ? '#fff' : '#fafafa'
              const marca = marcaReloj(linea)
              return (
                <tr key={linea.empleadoId} style={{ background: rowBg }}>

                  {/* Código del empleado, con el consecutivo interno (EMPn) debajo */}
                  <td style={{ ...TD, textAlign: 'center' }}>
                    <div style={{ fontWeight: 500, fontSize: 12 }}>
                      {linea.codigo == null ? '' : String(linea.codigo).padStart(4, '0')}
                    </div>
                    <div style={{ fontSize: 10, color: '#aaa' }}>{linea.serieNumeroEmpleado || ''}</div>
                  </td>

                  {/* Nombre — doble clic abre sobre-recibo */}
                  <td
                    style={{
                      ...TD, cursor: 'pointer',
                      ...(marca && { background: marca.fondo[idx % 2], boxShadow: `inset 3px 0 0 ${marca.color}` }),
                    }}
                    title={marca?.titulo}
                    onDoubleClick={() => onAbrirSobreRecibo(linea)}
                  >
                    <div style={{ fontWeight: 500, fontSize: 12 }}>
                      {linea.apellidoPaterno} {linea.apellidoMaterno} {linea.nombre}
                    </div>
                    <div style={{ fontSize: 10, color: '#aaa' }}>{linea.rfc}</div>
                    {linea.tieneModificaciones && (
                      <Tag color="blue" style={{ fontSize: 9, marginTop: 2, padding: '0 4px' }}>M</Tag>
                    )}
                  </td>

                  {/* Columnas dinámicas */}
                  {columnas.map(col => {
                    const { valor, importe, esManualValor, esManualImporte } =
                      this.getCeldaValores(linea, col)

                    const estilo = (esManual) => ({
                      color:      esManual ? '#1677ff' : 'inherit',
                      fontWeight: esManual ? 600 : 400,
                    })

                    if (col.mostrarValor && col.mostrarImporte) {
                      return [
                        <td key={`${col.conceptoNominaId}-v`} style={TD_NUM}>
                          <span style={estilo(esManualValor)}>{fmt(valor)}</span>
                        </td>,
                        <td key={`${col.conceptoNominaId}-i`} style={TD_NUM}>
                          <span style={estilo(esManualImporte)}>{fmt(importe)}</span>
                        </td>,
                      ]
                    }
                    const esMaActivo = col.mostrarValor ? esManualValor : esManualImporte
                    const valMostrar = col.mostrarValor ? valor : importe
                    return (
                      <td key={`${col.conceptoNominaId}-solo`} style={TD_NUM}>
                        <span style={estilo(esMaActivo)}>{fmt(valMostrar)}</span>
                      </td>
                    )
                  })}

                  {/* Otras Percepciones */}
                  <td style={{ ...TD_NUM, background: '#f0f5ff', color: '#2e54a0' }}>
                    {fmt(linea.otrasPercepciones || 0)}
                  </td>
                  {/* Otras Deducciones */}
                  <td style={{ ...TD_NUM, background: '#fff1f0', color: '#a32d2d' }}>
                    {fmt(linea.otrasDeducciones || 0)}
                  </td>
                  {/* Neto */}
                  <td style={{ ...TD_NUM, fontWeight: 600 }}>
                    {fmt(this.getNetoLocal(linea))}
                  </td>
                  {/* Estatus */}
                  <td style={{ ...TD, textAlign: 'center' }}>
                    {linea.estatus === 'calculada'  && <Tag color="green"   style={{ fontSize: 10 }}>Calculada</Tag>}
                    {linea.estatus === 'modificada' && <Tag color="blue"    style={{ fontSize: 10 }}>Modificada</Tag>}
                    {linea.estatus === 'pendiente'  && <Tag color="default" style={{ fontSize: 10 }}>Pendiente</Tag>}
                    {linea.estatus === 'timbrada'   && (
                      <Tooltip title={linea.uuid || ''} mouseEnterDelay={5}>
                        <Tag color="purple" style={{ fontSize: 10 }}><SafetyCertificateOutlined /> Timbrada</Tag>
                      </Tooltip>
                    )}
                    {linea.estatus === 'historica'  && (
                      <Tooltip title="Nómina histórica aplicada: sus acumulados ya se cargaron" mouseEnterDelay={5}>
                        <Tag color="cyan" style={{ fontSize: 10 }}>Histórica</Tag>
                      </Tooltip>
                    )}
                    {linea.estatus === 'sin_timbre' && (
                      // Gris a propósito: ni morado (se leería como timbrada) ni rojo
                      // (como error). No falló nada y no hay nada que reintentar.
                      <Tooltip
                        mouseEnterDelay={0}
                        overlayStyle={{ maxWidth: 420 }}
                        title={
                          <div style={{ fontSize: 11, lineHeight: 1.45 }}>
                            El recibo salió sin percepciones, así que no se mandó al PAC:
                            un CFDI que no ampara nada lo rechaza el SAT. No cuenta como
                            pendiente. Si esperabas pago, revisa sus incidencias y vuelve
                            a calcular.
                          </div>
                        }
                      >
                        <Tag color="default" style={{ fontSize: 10 }}>Sin timbre</Tag>
                      </Tooltip>
                    )}
                    {linea.estatus === 'error_timbrado' && (
                      // Sin mouseEnterDelay: este tag es el único lugar donde se
                      // lee por qué el PAC rechazó el timbre, y se muestran los
                      // DOS mensajes — el código ('CFDI40999 - Error no
                      // clasificado') no dice nada sin el detalle, que es el que
                      // nombra el atributo que hay que corregir.
                      <Tooltip
                        mouseEnterDelay={0}
                        overlayStyle={{ maxWidth: 460 }}
                        title={
                          <div style={{ fontSize: 11, lineHeight: 1.45 }}>
                            <div style={{ fontWeight: 600 }}>
                              {linea.errorTimbrado || 'Error de timbrado'}
                            </div>
                            {linea.errorTimbradoDetalle && (
                              <div style={{ marginTop: 4 }}>{linea.errorTimbradoDetalle}</div>
                            )}
                            {onReintentarLinea && (
                              <div style={{ marginTop: 6, opacity: 0.75 }}>
                                Corrige el dato y usa el botón de reintentar de este renglón.
                              </div>
                            )}
                          </div>
                        }
                      >
                        <Tag color="red" style={{ fontSize: 10 }}><WarningOutlined /> Error</Tag>
                      </Tooltip>
                    )}
                  </td>
                  {/* Acciones */}
                  <td style={{ ...TD, textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: 3, justifyContent: 'center' }}>
                      {linea.estatus === 'timbrada' ? (
                        <>
                          <Tooltip title="Sobre-recibo" mouseEnterDelay={5}>
                            <ButtonItem size="small" icon={<EditOutlined />}
                              onClick={() => onAbrirSobreRecibo(linea)} />
                          </Tooltip>
                          <Tooltip title="Ver recibo PDF" mouseEnterDelay={5}>
                            <ButtonItem size="small" icon={<FilePdfOutlined />}
                              onClick={() => onImprimirRecibo(linea._id)} />
                          </Tooltip>
                          {/* Lo de menos uso —y lo destructivo— detrás del "⋯":
                              eran cinco iconos por renglón × N empleados. */}
                          <Dropdown
                            trigger={['click']}
                            overlay={(
                              <Menu>
                                <Menu.Item key="cfdi" icon={<SafetyCertificateOutlined />}
                                  onClick={() => onAbrirCfdi(linea)}>
                                  Ver CFDI
                                </Menu.Item>
                                <Menu.Item key="xml" icon={<DownloadOutlined />}
                                  onClick={() => onDescargarXml(linea._id)}>
                                  Descargar XML
                                </Menu.Item>
                                <Menu.Divider />
                                <Menu.Item key="cancelar" danger icon={<CloseCircleOutlined />}
                                  disabled={cerrando || cancelando}
                                  onClick={() => onCancelarCfdi(linea)}>
                                  Cancelar CFDI ante el SAT
                                </Menu.Item>
                              </Menu>
                            )}
                          >
                            <ButtonItem size="small" icon={<MoreOutlined />} />
                          </Dropdown>
                        </>
                      ) : (
                        <>
                          {/* Reintentar sólo esta línea. No se filtra por
                              soloLectura: una línea en error vive justo en los
                              estatus de solo lectura ('cerrada' /
                              'parcialmente_timbrada'), y ahí es donde se
                              necesita el reintento. */}
                          {linea.estatus === 'error_timbrado' && onReintentarLinea && (
                            <Tooltip title="Reintentar el timbrado de esta línea" mouseEnterDelay={0}>
                              <ButtonItem ambar size="small" icon={<SyncOutlined />}
                                disabled={cerrando || cancelando}
                                onClick={() => onReintentarLinea(linea)} />
                            </Tooltip>
                          )}
                          {!soloLectura && (
                            <Tooltip title="Recalcular esta línea" mouseEnterDelay={5}>
                              <ButtonItem size="small" icon={<ReloadOutlined />} loading={calculando || cerrando}
                                onClick={() => onCalcularLinea(linea.empleadoId)} />
                            </Tooltip>
                          )}
                          <Tooltip title="Sobre-recibo" mouseEnterDelay={5}>
                            <ButtonItem size="small" icon={<EditOutlined />}
                              onClick={() => onAbrirSobreRecibo(linea)} />
                          </Tooltip>
                          {/* Mismo criterio que en la rama de timbradas: inline sólo
                              lo de uso diario, el resto detrás del "⋯". El menú no se
                              pinta si no queda nada adentro. */}
                          {(() => {
                            const extras = [
                              // El recibo también se puede ver antes de timbrar: sale con
                              // marca de agua de vista previa. Se omite en 'pendiente'
                              // porque esa línea no tiene conceptos calculados y saldría
                              // en ceros.
                              linea.estatus !== 'pendiente' && (
                                <Menu.Item key="recibo" icon={<FilePdfOutlined />}
                                  onClick={() => onImprimirRecibo(linea._id)}>
                                  Ver recibo PDF (sin timbrar)
                                </Menu.Item>
                              ),
                              // Timbrar esta línea histórica. Igual que el reintento, NO se
                              // filtra por soloLectura: una nómina histórica aplicada está
                              // justo en uno de esos estatus, y ahí es donde hace falta.
                              // Sólo aparece con el complemento prendido; la advertencia
                              // de lo que implica va en la confirmación.
                              linea.estatus === 'historica' && puedeTimbrarLineaHistorica
                                && onTimbrarLineaHistorica && (
                                <Menu.Item key="timbrar-historica" icon={<SafetyCertificateOutlined />}
                                  disabled={cerrando || cancelando || !!timbrandoLineaHistorica}
                                  onClick={() => onTimbrarLineaHistorica(linea)}>
                                  Timbrar esta línea (emite un CFDI real)
                                </Menu.Item>
                              ),
                              !soloLectura && (
                                <Menu.Item key="quitar" danger icon={<DeleteOutlined />}
                                  onClick={() => onQuitarEmpleado(linea.empleadoId)}>
                                  Quitar empleado
                                </Menu.Item>
                              ),
                            ].filter(Boolean)
                            if (extras.length === 0) return null
                            return (
                              <Dropdown trigger={['click']} overlay={<Menu>{extras}</Menu>}>
                                <ButtonItem size="small" icon={<MoreOutlined />}
                                  loading={timbrandoLineaHistorica === linea._id} />
                              </Dropdown>
                            )
                          })()}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              )
            })}

            {lineasVisibles.length === 0 && (
              <tr>
                <td style={{ ...TD, textAlign: 'center', color: '#aaa', padding: 24 }}
                  colSpan={7 + subColsDinamicas}>
                  Ningún empleado coincide con la búsqueda
                </td>
              </tr>
            )}
          </tbody>

          {/* Totales de la sección — siempre de todas sus líneas, nunca del filtro */}
          <tfoot>
            <tr style={{ background: '#f0f0f0', fontWeight: 700 }}>
              {/* Celda que abarca: Cód. + Empleado + todas las sub-columnas dinámicas */}
              <td style={{ ...TD, fontWeight: 700 }} colSpan={2 + subColsDinamicas}>
                {opciones.etiquetaTotales || 'TOTALES'} — {lineasTotales.length} empleados
                {/* Con filtro activo hay que decirlo: un total parcial se puede leer por
                    error como el total de la nómina. */}
                {this.state.filtroEmpleado.trim() !== '' && (
                  <span style={{ fontWeight: 400, color: '#888' }}> (todas las líneas)</span>
                )}
              </td>
              {/* Suma de otrasPercepciones */}
              <td style={{ ...TD_NUM, background: '#f0f5ff', color: '#2e54a0' }}>
                {fmt(lineasTotales.reduce((s, l) => s + (l.otrasPercepciones || 0), 0))}
              </td>
              {/* Suma de otrasDeducciones */}
              <td style={{ ...TD_NUM, background: '#fff1f0', color: '#a32d2d' }}>
                {fmt(lineasTotales.reduce((s, l) => s + (l.otrasDeducciones || 0), 0))}
              </td>
              {/* Neto total */}
              <td style={{ ...TD_NUM }}>
                {fmt(lineasTotales.reduce((s, l) => s + this.getNetoLocal(l), 0))}
              </td>
              <td style={TD} colSpan={2} />
            </tr>
          </tfoot>
        </table>
      </div>
    )
  }

  // ─────────────────────────────────────────────────────────────────────────
  // VISTA 2 — Tabla de Días e Incidencias
  //   Filas: empleados
  //   Columnas: cada día del periodo
  //   Celda: PopoverIncidencia (resumen compacto + edición al click)
  // ─────────────────────────────────────────────────────────────────────────
  /**
   * Mismo corte que en la vista de Importes: la nómina ordinaria arriba y los finiquitos abajo,
   * en su propia tabla. Un empleado liquidado casi siempre tiene el periodo a medias —se va a
   * media semana— y sus celdas grises fuera de la relación laboral se leían como huecos de la
   * nómina normal.
   *
   * La leyenda va UNA sola vez, arriba de las dos: es la misma para ambas.
   * Sin finiquitos en la prenómina esto devuelve una sola tabla, la de siempre.
   */
  renderTablaIncidencias() {
    const dias      = this.getDias()
    const tiposInc  = this.getTiposInc()
    const septimos  = this.getSeptimos()
    const lineasVisibles = this.getLineasFiltradas()
    const todas = this.props.prenomina.lineas || []
    // La nota de la leyenda sólo aparece si hay alguien con alta o baja dentro del periodo:
    // en la mayoría de las nóminas no hay ninguna celda gris que explicar.
    const hayFueraDeRelacion = lineasVisibles.some(l => dias.some(d => this.fueraDeRelacion(l, d)))
    // Igual: la nota del reloj sólo aparece si ya se importó asistencia en esta prenómina.
    const hayMarcaReloj = lineasVisibles.some(l => marcaReloj(l))

    if (!dias.length) {
      return (
        <div style={{ textAlign: 'center', padding: 40, color: '#aaa' }}>
          La prenómina no tiene días de periodo configurados.
        </div>
      )
    }

    const esFin = (l) => !!l.finiquitoId
    const hayFiniquitos = todas.some(esFin)
    const contexto = { dias, tiposInc, septimos }

    return (
      <>
        {/* Leyenda de abreviaturas */}
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 8,
          marginBottom: 10, fontSize: 11,
        }}>
          {tiposInc.map(t => (
            <span key={t.tipo} style={{ color: t.color }}>
              <b>{t.abrev}</b> = {t.label}
            </span>
          ))}
          <span style={{ color: '#aaa', marginLeft: 8 }}>
            · Click en una celda para editar incidencias de ese día · Doble clic en el nombre
            para abrir el sobre-recibo
          </span>
          {septimos.size > 0 && (
            <span style={{ color: '#1677ff' }}>
              · Las columnas azules <b>7º</b> son el día de descanso: lo que se capture ahí no
              descuenta sueldo ni séptimo día, pero sí cuenta para el ausentismo de IMSS
            </span>
          )}
          {hayFueraDeRelacion && (
            <span style={{ color: '#999' }}>
              · Las celdas grises quedan fuera de la relación laboral del empleado (antes de su
              alta o después de su baja) y no se pueden capturar
            </span>
          )}
          {hayMarcaReloj && (
            <>
              <span style={{ color: MARCA_RELOJ.calculada.color }}>
                · Nombre en <b>verde</b>: su asistencia se calculó con el reloj checador
              </span>
              <span style={{ color: MARCA_RELOJ.noCalculada.color }}>
                · Nombre en <b>rojo</b>: no se calculó porque no tiene reloj asignado
              </span>
            </>
          )}
        </div>

        {hayFiniquitos ? (
          <>
            {this.renderTablaDeIncidencias(lineasVisibles.filter(l => !esFin(l)), {
              ...contexto,
              titulo: 'Nómina ordinaria',
              subtitulo: 'Sin las liquidaciones del periodo',
              cuenta: todas.filter(l => !esFin(l)).length,
            })}
            {this.renderTablaDeIncidencias(lineasVisibles.filter(esFin), {
              ...contexto,
              titulo: 'Finiquitos',
              subtitulo: 'Su relación laboral termina dentro del periodo',
              cuenta: todas.filter(esFin).length,
              acento: '#7a4fbf',
            })}
          </>
        ) : this.renderTablaDeIncidencias(lineasVisibles, contexto)}
      </>
    )
  }

  renderTablaDeIncidencias(lineasVisibles, opciones = {}) {
    const { soloLectura, incidenciasSoloLectura, onAbrirSobreRecibo } = this.props
    const { dias, tiposInc, septimos } = opciones

    return (
      // Cada sección con su propio scroll horizontal, como las tablas de Importes: dos tablas
      // dentro de un mismo contenedor con `tableLayout: auto` dimensionan sus columnas por
      // separado, y bastaría un nombre más largo de un lado para que los días dejaran de
      // coincidir entre una y otra.
      <div style={{ overflowX: 'auto', marginBottom: opciones.titulo ? 14 : 0 }}>
        {opciones.titulo && renderTituloSeccion({
          titulo: opciones.titulo,
          subtitulo: opciones.subtitulo,
          cuenta: opciones.cuenta,
          acento: opciones.acento || '#005F6B',
        })}

        <table style={{ borderCollapse: 'collapse', tableLayout: 'auto' }}>
          <thead>
            <tr>
              {/* Columnas fijas: código y nombre del empleado. El ancho de la primera
                  es el offset 'left' de la segunda — de ahí ANCHO_COL_CODIGO. */}
              {this.renderThCodigo({
                estilo: {
                  position: 'sticky', left: 0, zIndex: 3,
                  background: '#f5f5f5',
                },
              })}
              {this.renderThOrdenable('nombre', 'Empleado', {
                estilo: {
                  textAlign: 'left', minWidth: 180,
                  position: 'sticky', left: ANCHO_COL_CODIGO, zIndex: 3,
                  background: '#f5f5f5',
                },
              })}

              {/* Una columna por día del periodo */}
              {dias.map(d => {
                const fecha  = this.getFecha(d)
                const esFin  = fecha && (fecha.day() === 0 || fecha.day() === 6)
                const esSep  = septimos.has(d)
                return (
                  <th
                    key={d}
                    title={esSep
                      ? 'Día de descanso (séptimo día). Lo que se capture aquí no descuenta sueldo ni séptimo día, pero sí cuenta para el ausentismo de IMSS.'
                      : undefined}
                    style={{
                      ...TH, width: 48, minWidth: 44,
                      background: esSep ? '#e6f4ff' : esFin ? '#fff7e6' : '#f5f5f5',
                      borderTop: esSep ? '2px solid #1677ff' : TH.border,
                      borderBottom: '2px solid #d0d0d0',
                    }}
                  >
                    <div style={{ fontSize: 12 }}>{fecha ? fecha.format('DD') : d}</div>
                    <div style={{ fontSize: 9, color: esSep ? '#1677ff' : esFin ? '#faad14' : '#aaa', fontWeight: 400 }}>
                      {fecha ? DIAS_ES[fecha.day()] : ''}
                    </div>
                    {esSep && <div style={{ fontSize: 9, color: '#1677ff', fontWeight: 700, lineHeight: 1.1 }}>7º</div>}
                  </th>
                )
              })}

              {/* Resumen de totales por empleado */}
              <th style={{ ...TH, minWidth: 90, background: '#e6f7ff', position: 'sticky', right: 0 }}>
                Resumen
              </th>
            </tr>
          </thead>

          <tbody>
            {lineasVisibles.map((linea, idx) => {
              const marca = marcaReloj(linea)
              // Contar totales de incidencias del empleado
              const totalesTipo = {}
              const idATipoResumen = {}
              for (const t of tiposInc) {
                totalesTipo[t.tipo] = 0
                if (t.id) idATipoResumen[t.id] = t.tipo
              }
              for (const dia of (linea.incidencias || [])) {
                for (const e of dia.entradas) {
                  const tipoKey = idATipoResumen[e.tipoIncidenciaId] ?? e.tipo
                  if (tipoKey !== undefined && totalesTipo[tipoKey] !== undefined) totalesTipo[tipoKey] += e.cantidad
                }
              }
              const resumenFila = tiposInc
                .filter(t => totalesTipo[t.tipo] > 0)
                .map(t => (
                  <div key={t.tipo} style={{ fontSize: 10, color: t.color, fontWeight: 600 }}>
                    {t.abrev}: {totalesTipo[t.tipo]}
                  </div>
                ))

              return (
                <tr key={linea.empleadoId} style={{ background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>

                  {/* Código (fijo a la izquierda). El background explícito es obligatorio en
                      las celdas sticky: sin él se transparentan sobre las columnas de días. */}
                  <td style={{
                    ...TD, textAlign: 'center',
                    position: 'sticky', left: 0,
                    background: idx % 2 === 0 ? '#fff' : '#fafafa',
                    zIndex: 1,
                  }}>
                    <div style={{ fontWeight: 500, fontSize: 12 }}>
                      {linea.codigo == null ? '' : String(linea.codigo).padStart(4, '0')}
                    </div>
                    <div style={{ fontSize: 10, color: '#aaa' }}>{linea.serieNumeroEmpleado || ''}</div>
                  </td>

                  {/* Nombre (fijo a la izquierda, después del código) — doble clic abre el
                      sobre-recibo, igual que en la vista de Importes. */}
                  <td
                    style={{
                      ...TD, cursor: 'pointer',
                      position: 'sticky', left: ANCHO_COL_CODIGO,
                      background: marca ? marca.fondo[idx % 2] : (idx % 2 === 0 ? '#fff' : '#fafafa'),
                      boxShadow: marca ? `inset 3px 0 0 ${marca.color}` : undefined,
                      borderRight: '2px solid #d0d0d0',
                      zIndex: 1,
                    }}
                    title={marca?.titulo}
                    onDoubleClick={() => onAbrirSobreRecibo(linea)}
                  >
                    <div style={{ fontWeight: 500, fontSize: 12, whiteSpace: 'nowrap' }}>
                      {linea.apellidoPaterno} {linea.apellidoMaterno} {linea.nombre}
                    </div>
                    <div style={{ fontSize: 10, color: '#aaa' }}>{linea.rfc}</div>
                  </td>

                  {/* Celda por día */}
                  {dias.map(d => {
                    const fecha    = this.getFecha(d)
                    const esFin    = fecha && (fecha.day() === 0 || fecha.day() === 6)
                    const esSep    = septimos.has(d)
                    const entradas = this.getEntradasDia(linea, d)
                    const fuera    = this.fueraDeRelacion(linea, d)

                    return (
                      <td
                        key={d}
                        title={fuera
                          ? 'Fuera de la relación laboral del empleado: no se paga ni cotiza'
                          : esSep
                            ? 'Día de descanso (séptimo día): no descuenta sueldo ni séptimo día'
                            : undefined}
                        style={{
                          ...TD, padding: '1px 2px',
                          background: fuera
                            ? '#f0f0f0'
                            : esSep
                              ? (idx % 2 === 0 ? '#f5fbff' : '#ecf7ff')
                              : esFin
                                ? (idx % 2 === 0 ? '#fff9f0' : '#fff3e0')
                                : (idx % 2 === 0 ? '#fff' : '#fafafa'),
                        }}
                      >
                        <CeldaIncidencia
                          entradas={entradas}
                          tiposInc={tiposInc}
                          soloLectura={fuera || soloLectura || incidenciasSoloLectura || ['timbrada', 'historica'].includes(linea.estatus)}
                          onGuardar={(nuevasEntradas) =>
                            this.props.onGuardarIncidenciaDia(linea.empleadoId, d, fecha?.toISOString(), nuevasEntradas)
                          }
                          onAbrirModal={() => this.abrirModalInc(linea, d, fecha)}
                        />
                      </td>
                    )
                  })}

                  {/* Resumen del empleado (fijo a la derecha) */}
                  <td style={{
                    ...TD,
                    position: 'sticky', right: 0,
                    background: idx % 2 === 0 ? '#e6f7ff' : '#d6f0ff',
                    borderLeft: '2px solid #91caff',
                    zIndex: 1,
                    minWidth: 70,
                  }}>
                    {resumenFila.length > 0 ? resumenFila : (
                      <span style={{ color: '#ccc', fontSize: 10 }}>Sin inc.</span>
                    )}
                  </td>
                </tr>
              )
            })}

            {lineasVisibles.length === 0 && (
              <tr>
                <td style={{ ...TD, textAlign: 'center', color: '#aaa', padding: 24 }}
                  colSpan={3 + dias.length}>
                  Ningún empleado coincide con la búsqueda
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    )
  }

  // ─────────────────────────────────────────────────────────────────────────
  render() {
    const { prenomina } = this.props
    if (!prenomina) return null

    const { vistaActiva } = this.state

    return (
      <>
        {this.renderBarra()}
        {vistaActiva === 'importes'    && this.renderTablaImportes()}
        {vistaActiva === 'incidencias' && this.renderTablaIncidencias()}
        {this.renderModalInc()}
        <ModalImportarAsistencia
          visible={this.state.modalImportarAsistenciaVisible}
          prenomina={prenomina}
          onCerrar={() => this.setState({ modalImportarAsistenciaVisible: false })}
          onImportar={(opciones) => this.props.onImportarIncidenciasAsistencia(opciones)}
        />
        <ModalImportarIncidenciasExcel
          visible={this.state.modalImportarExcelVisible}
          prenomina={prenomina}
          tiposIncidencias={this.props.tiposIncidencias}
          onCerrar={() => this.setState({ modalImportarExcelVisible: false })}
          onImportar={(payload) => this.props.onImportarIncidenciasExcel(payload)}
        />
      </>
    )
  }
}

export default TablaPrenomina